/**
 * SCHADS pay calculator — pure functions for computing worker pay and
 * validating roster compliance against the Social, Community, Home Care and
 * Disability Services Industry Award MA000100.
 *
 * Base hourly rates select the national-catalogue snapshot for the shift's
 * local start date; explicit catalogue/EBA overrides take precedence.
 *
 * Scope of v1 (this file):
 *  ✓ Base pay = hours × level rate
 *  ✓ Casual loading (25%) embedded in casual penalty rates
 *  ✓ Saturday / Sunday / public holiday penalty rates (permanent + casual)
 *  ✓ Evening / night shift loadings (shiftworkers only)
 *  ✓ Mixed shifts crossing day boundaries (Sat → Sun, Fri 8pm → Sat 2am, etc.)
 *  ✓ Sleepover flat allowance
 *  ✓ Broken shift flat allowance (1 break / 2+ breaks)
 *  ✓ Travel-per-km reimbursement
 *  ✓ On-call weekday / weekend allowances
 *  ✓ Minimum engagement validation (2h casual + home care PT, 3h full time)
 *  ✓ Maximum ordinary hours per day (10h)
 *  ✓ Maximum spread of hours (12h)
 *  ✓ Minimum break between shifts (10h)
 *
 * NOT yet implemented (future iterations):
 *  ✗ Overtime across weekly 38h threshold (requires querying the rest of the
 *    worker's week — do this at the server-action layer, not in the pure fn)
 *  ✗ Pay-point progression beyond the flat level rate (SACS has pay points;
 *    Schedule F home care aged care has flat rates per level so this is only
 *    relevant to SACS-level workers — rarely the case in home care)
 *  ✗ Public holiday detection (caller passes `isPublicHoliday` — callers
 *    should consult a state-specific public_holidays table)
 *  ✗ First aid allowance (weekly, not per-shift)
 *  ✗ Uniform/laundry weekly caps (apply at payroll run, not per shift)
 *  ✗ Aboriginal/remote location supplements
 *
 * Citation: Fair Work Pay Guide G01002983 published 04/09/2025, effective
 * first full pay period on or after 01/10/2025. Verified 2026-04-07.
 */
import {
  schadsRateScheduleFor,
  SCHADS_LEVEL_RATES,
  SCHADS_HOME_CARE_AGED_RATES,
  SCHADS_HOME_CARE_DISABILITY_RATES,
  SCHADS_PERMANENT_PENALTY_RATES,
  SCHADS_CASUAL_PENALTY_RATES,
  SCHADS_OVERTIME_RATES,
  SCHADS_ALLOWANCES,
  ATO_CENTS_PER_KM_2026_27,
  SCHADS_VEHICLE_AWARD_FLOOR_2026_27,
  SCHADS_KM_FY_EXEMPT_LIMIT,
  SCHADS_BROKEN_SHIFT_FLAT,
  SCHADS_MINIMUM_SHIFT_HOURS,
  SCHADS_ROSTER_LIMITS,
  SCHADS_LEAVE,
} from './regulatory-rates';
import { roundMoney, sumMoney } from './money-rounding';
import { DEFAULT_TZ, dateOnlyInTz } from './timezone';
import { classifyShift, type ShiftCharacterRules } from './award-engines/shift-classifier';
import {
  PR798459_MAX_ORDINARY_EITHER_SIDE,
  PR798459_TWELVE_HOUR_ORDINARY_CAP,
  isPr798459Operative,
  isSleepoverFlankSameShift,
  resolvedSleepoverWindow,
  workSegmentsOutsideSleepover,
} from './schads-pr798459';
import {
  calculateSchadsRemoteWorkPay,
  isSchadsRemoteWork,
  remoteWorkDoesNotCountForRest,
} from './schads-remote-work';
import type { SchadsRemoteWorkKind } from './schads-remote-work';
import { canonicalAllowanceCode } from './allowance-code-canonical';
import {
  payrollBreakMinutesForEngine,
  type BreakSemantics,
  type BreakTreatmentSnapshotV1,
} from './payroll/break-treatment';
import {
  validateResolvedOrdinaryHoursCapWindows,
  type ResolvedOrdinaryHoursCapWindow,
} from './payroll/ordinary-hours-cap-windows';
import {
  getCertGateRequirement,
  isCertGateSatisfied,
  type ActiveCertificationInput,
  type CertGateRegistry,
} from './cert-currency';
import { schadsAllowancesForShift } from './payroll/wage-related-allowances';

/** Day allocation remains per segment; shift loading uses the whole work period. */
const SCHADS_SHIFT_RULES: ShiftCharacterRules = {};

export type SchadsShiftLoadingCharacter = 'DAY' | 'AFTERNOON' | 'NIGHT';

/**
 * MA000100 cl 29.2(a)/(b): afternoon finishes after 8 pm and at or before
 * midnight; night finishes after midnight or commences before 6 am.
 * Weekends and public holidays substitute their rates at the segment boundary.
 * Settlement fragments retain the assignment envelope. The operative sleepover
 * window splits that envelope into independently classified work portions.
 */
export function schadsShiftLoadingCharacters(
  input: ShiftPayInput,
): Record<'before' | 'after' | 'undivided', SchadsShiftLoadingCharacter> {
  const timezone = input.timezone ?? DEFAULT_TZ;
  const broken = Boolean(input.shiftLoadingContext?.hasBrokenShift ?? input.hasBrokenShift);
  const start = broken
    ? input.shiftLoadingContext?.workPeriodStart
      ? new Date(input.shiftLoadingContext.workPeriodStart)
      : input.startTime
    : (input.shiftEnvelopeStartTime ?? input.startTime);
  const end = broken
    ? input.shiftLoadingContext?.workPeriodEnd
      ? new Date(input.shiftLoadingContext.workPeriodEnd)
      : input.endTime
    : (input.shiftEnvelopeEndTime ?? input.endTime);
  const window = resolvedSleepoverWindow(
    input.shiftLoadingContext?.hasSleepover ?? input.hasSleepover,
    input.sleepoverStart,
    input.sleepoverEnd,
    start,
    timezone,
  );
  const clock = (at: Date): number => {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: timezone,
      hourCycle: 'h23',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(at);
    const value = (kind: string) => Number(parts.find((part) => part.type === kind)?.value ?? 0);
    return (
      value('hour') * 3600 + value('minute') * 60 + value('second') + at.getUTCMilliseconds() / 1000
    );
  };
  const character = (from: Date, to: Date): SchadsShiftLoadingCharacter => {
    if (to.getTime() <= from.getTime()) return 'DAY';
    const finish = clock(to);
    const crossesDate = dateOnlyInTz(to, timezone) > dateOnlyInTz(from, timezone);
    // Exactly midnight is included in afternoon, rather than "after" midnight.
    // Broken-shift periods commencing before 6 am do not receive night loading.
    if (broken && clock(from) < 6 * 3600) return 'DAY';
    if (clock(from) < 6 * 3600 || (crossesDate && finish > 0)) return 'NIGHT';
    if (finish > 20 * 3600 || (crossesDate && finish === 0)) return 'AFTERNOON';
    return 'DAY';
  };
  return {
    undivided: character(start, end),
    before: window ? character(start, window.start) : 'DAY',
    after: window ? character(window.end, end) : 'DAY',
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Normalized SCHADS level format — one of the valid keys in
 * SCHADS_LEVEL_RATES, SCHADS_HOME_CARE_AGED_RATES, or SCHADS_HOME_CARE_DISABILITY_RATES.
 */
export type NormalizedSchadsLevel =
  | keyof typeof SCHADS_LEVEL_RATES
  | keyof typeof SCHADS_HOME_CARE_AGED_RATES
  | keyof typeof SCHADS_HOME_CARE_DISABILITY_RATES;

export type SchadsEmploymentType = 'permanent' | 'casual';
export type SchadsEmployeeCategory = 'dayworker' | 'shiftworker';

/**
 * Phase 1 §4.3 — Optional override for SCHADS rate / penalty / allowance
 * constants. When `undefined`, the calculator uses
 * the dated national snapshot for the shift's local start date. When provided,
 * the override values take precedence.
 *
 * The intended caller is the pay-run snapshot path, which builds an override
 * from the active `award_rate_versions` row for the shift date via
 * `buildOverrideFromContext()` in `award-rate-lookup.ts`. The dual-run
 * regression test in `__tests__/dual-run.test.ts` proves byte-equality
 * between the constants path and a ctx-derived override path.
 *
 * Each field is optional — partial overrides are supported. Unset fields
 * fall back to constants. (For Phase 1 the override is all-or-nothing in
 * practice; the partial-merge support keeps the door open for EBA overrides
 * that only change selected allowances.)
 */
/**
 * Widen `as const` literal-typed objects to plain `number`-valued shapes —
 * the override is a runtime substitute, not a one-of-the-constants alias.
 */
export type WidenToNumber<T> = { -readonly [K in keyof T]: number };

export interface SchadsRatesOverride {
  /** Derived from the resolved catalogue header, never an allowance line. */
  weeklyMinimumScopeKey?: string;
  levelRates?: Record<string, number>;
  homeCareAgedRates?: Record<string, number>;
  homeCareDisabilityRates?: Record<string, number>;
  permanentPenaltyRates?: WidenToNumber<typeof SCHADS_PERMANENT_PENALTY_RATES>;
  casualPenaltyRates?: WidenToNumber<typeof SCHADS_CASUAL_PENALTY_RATES>;
  overtimeRates?: WidenToNumber<typeof SCHADS_OVERTIME_RATES>;
  allowances?: WidenToNumber<typeof SCHADS_ALLOWANCES>;
  brokenShiftFlat?: WidenToNumber<typeof SCHADS_BROKEN_SHIFT_FLAT>;
}

interface ResolvedRates {
  levelRates: Record<string, number>;
  homeCareAgedRates: Record<string, number>;
  homeCareDisabilityRates: Record<string, number>;
  permanentPenaltyRates: WidenToNumber<typeof SCHADS_PERMANENT_PENALTY_RATES>;
  casualPenaltyRates: WidenToNumber<typeof SCHADS_CASUAL_PENALTY_RATES>;
  overtimeRates: WidenToNumber<typeof SCHADS_OVERTIME_RATES>;
  allowances: WidenToNumber<typeof SCHADS_ALLOWANCES>;
  brokenShiftFlat: WidenToNumber<typeof SCHADS_BROKEN_SHIFT_FLAT>;
}

/**
 * Resolve the rate set for a calculator call: every field defaults to its
 * dated base hourly schedule and provision constants unless overridden.
 *
 * Wage-related allowances (sleepover, on-call, first-aid, broken-shift)
 * switch to AWR 2026 computed amounts from 1 July 2026 when no override
 * is supplied. Expense/CPI flats stay on the Jul 2025 vintage. A
 * RateContext / `SchadsRatesOverride` still wins — tenant/EBA rows are
 * not overwritten.
 */
function resolveRates(
  override?: SchadsRatesOverride,
  asAt?: Date,
  timezone: string = DEFAULT_TZ,
): ResolvedRates {
  const schedule = schadsRateScheduleFor(asAt ? dateOnlyInTz(asAt, timezone) : undefined);
  const vintage = asAt
    ? schadsAllowancesForShift(asAt, timezone)
    : schadsAllowancesForShift(new Date(0));
  return {
    levelRates: override?.levelRates ?? schedule.levelRates,
    homeCareAgedRates: override?.homeCareAgedRates ?? schedule.homeCareAgedRates,
    homeCareDisabilityRates: override?.homeCareDisabilityRates ?? schedule.homeCareDisabilityRates,
    permanentPenaltyRates: override?.permanentPenaltyRates ?? SCHADS_PERMANENT_PENALTY_RATES,
    casualPenaltyRates: override?.casualPenaltyRates ?? SCHADS_CASUAL_PENALTY_RATES,
    overtimeRates: override?.overtimeRates ?? SCHADS_OVERTIME_RATES,
    allowances: override?.allowances ?? vintage.allowances,
    brokenShiftFlat: override?.brokenShiftFlat ?? vintage.brokenShiftFlat,
  };
}

export interface ShiftPayInput {
  /** SCHADS level or home-care classification. Accepts messy formats; will be normalised. */
  schadsLevel: string;
  /**
   * Employment type. Valid: 'permanent' (full-time + part-time treated the same
   * for penalty rate purposes) or 'casual' (25% loading embedded in casual rates).
   */
  employmentType: SchadsEmploymentType;
  /**
   * Employee category per SCHADS clause 25.2 vs clause 29. 'shiftworker' gets
   * evening/night shift loadings; 'dayworker' gets overtime when working
   * outside the 6am-8pm span. Defaults to 'dayworker' if unspecified.
   */
  employeeCategory?: SchadsEmployeeCategory;
  /** True when the staff pay profile identifies the worker as a permanent-night worker. */
  permanentNightShiftWorker?: boolean;
  /** Shift start as a full Date (local time respected via `timezone`). */
  startTime: Date;
  /** Shift end (must be after startTime). */
  endTime: Date;
  /** Break duration captured from roster/timesheet evidence. Defaults to 0. */
  breakMinutes?: number;
  /** Actual timesheet break placement, when captured. Trace-only; no pricing change. */
  actualBreakStartTime?: Date;
  actualBreakEndTime?: Date;
  /**
   * Whole-shift envelope for majority / commencement / character classification
   * when pricing a settlement WORK fragment. Fragment `startTime`/`endTime` still
   * drive worked hours; envelope fields classify once from the assignment shift.
   */
  shiftEnvelopeStartTime?: Date;
  shiftEnvelopeEndTime?: Date;
  shiftEnvelopeBreakMinutes?: number;
  /** Classification facts retained on settlement fragments after once-only allowances are cleared. */
  shiftLoadingContext?: {
    hasBrokenShift?: boolean;
    hasSleepover?: boolean;
    /** ISO instants for one legal broken-shift work period, shared by adjacent fragments. */
    workPeriodStart?: string;
    workPeriodEnd?: string;
    ordinaryHoursBeforeSleepover?: number;
    ordinaryHoursAfterSleepover?: number;
  };
  /**
   * Provider EA / award-configured break treatment. When supplied, the
   * payroll lens in this snapshot decides how many break minutes are deducted
   * by the engine. The raw `breakMinutes` remains available for audit,
   * roster display and care-minute calculations.
   */
  breakTreatment?: BreakTreatmentSnapshotV1 | null;
  /**
   * R71 gate-to-legacy: how `breakTreatment` prices. Omitted = LEGACY_28D1
   * (tenant payroll rebuild OFF, production 28d166931e). Only rebuild-ON callers
   * pass V13.
   */
  breakSemantics?: BreakSemantics;
  /** True if the shift falls on a declared public holiday (state-specific). */
  isPublicHoliday?: boolean;
  /** Both-clock calendar spans, retained across settlement fragments and replay. */
  publicHolidaySegments?: readonly {
    startTime: string;
    endTime: string;
    isPublicHoliday: boolean;
  }[];
  /**
   * EBA conditional penalty inputs. These are no-ops for base-award SCHADS
   * and are consumed by ctx-backed Aged Care / Nurses EBA engines only when
   * the compiled pay-rule overlay contains the matching conditional block.
   */
  conditionalPenaltyCode?: string | null;
  shiftStartTime?: string | null;
  missedMealBreak?: boolean | null;
  continuousHoursWithoutMealBreak?: number | null;
  interruptedMealBreak?: boolean | null;
  interruptedMealBreakMinutes?: number | null;
  publicHolidayNotWorked?: boolean | null;
  usualRosteredHours?: number | null;
  /** IANA timezone for day-of-week determination. Defaults to Australia/Sydney. */
  timezone?: string;

  // Optional allowances — per SCHADS MA000100 Pay Guide G01002983:
  //   cl 20.6     — sleepover flat (hasSleepover)
  //   cl 20.7(a)  — vehicle per km (travelKm)
  //   cl 20.11    — on-call weekday/weekend (onCallWeekday/Weekend Days)
  //   cl 20.12    — broken shift (hasBrokenShift 0/1/2)
  //   cl 20.5     — delayed-meal flat (hasDelayedMealAllowance)
  //   cl 20.8     — first-aid weekly ($20.46 FT, $0.54/h PT capped weekly)
  //   cl 20.3     — uniform per shift ($1.23/shift, $6.24/wk cap)
  //   cl 20.3     — laundry per shift ($0.32/shift, $1.49/wk cap)
  hasSleepover?: boolean;
  /**
   * Sleepover period (cl 25.7). When set with `hasSleepover` on or after
   * 1 June 2026 (PR798459), work inside the window is dropped from ordinary
   * hours and shift loadings so cl 29.3(d) can test each flank separately.
   * Callers without a window keep the legacy single-span classification.
   */
  sleepoverStart?: Date;
  sleepoverEnd?: Date;
  /**
   * cl 25.1(c) + 28.1(b)(iii)–(iv): the employer and employee agreed a 12-hour
   * wrap with work immediately before and after the sleepover. Default false
   * keeps the 10-hour ordinary ceiling. Ignored before 1 June 2026.
   */
  agreedTwelveHourSleepoverShift?: boolean;
  /**
   * SCHADS cl 25.10 remote work. When true the engine uses the remote-work
   * minimum ladder and rates instead of the ordinary shift matrix, and
   * cl 10.5 minimum payments do not apply (cl 25.10(f)(ii)).
   */
  isRemoteWork?: boolean;
  /**
   * cl 25.10(c)(i) kind. Omitted remote work defaults to `not_on_call`
   * (1-hour minimum) so we do not underpay an untyped instance.
   */
  remoteWorkKind?: SchadsRemoteWorkKind;
  hasBrokenShift?: 0 | 1 | 2;
  travelKm?: number;
  /**
   * When true, the vehicle-km allowance is emitted as two STP-CD lines — a
   * PAYGW-exempt component (ATO rate) and a taxable residual — instead of one
   * KM_ALLOWANCE line. Tenant pay-policy driven; default OFF preserves the
   * legacy single-line emission byte-for-byte. The FY 5,000-km exemption cap
   * is applied at the pay-run layer by `applyVehicleKmFyExemptionCap`.
   */
  vehicleKmTaxSplit?: boolean;
  onCallWeekdayDays?: number;
  onCallWeekendDays?: number;
  hasDelayedMealAllowance?: boolean;
  /** First aid — FT kitbearer flat weekly ($20.46). PT/casual use hourlyPartTime. */
  hasFirstAidResponsibility?: boolean;
  /** True if worker wore the employer-supplied uniform on this shift. */
  hasUniformAllowance?: boolean;
  /** True if the employer required the worker to launder the uniform. */
  hasLaundryAllowance?: boolean;
  /**
   * True when the worker is engaged as full-time (as distinct from
   * part-time). Required to correctly dispatch the first-aid allowance
   * (cl 20.8) — FT workers are paid the weekly flat rate ÷ 5 per shift;
   * PT / casual are paid hourly rate × hours worked. `employmentType`
   * alone cannot be used because upstream callers collapse
   * full-time/part-time into a single `'permanent'` classification.
   */
  isFullTime?: boolean;
  /**
   * Phase 1H (Award Engine Integrity 2026-05) — SCHADS cl 25.5(f) home-care
   * visit cancellation. When set, the engine evaluates whether the
   * cancellation triggers payable-cancellation treatment based on the
   * notice period between `cancelledAt` and `scheduledStart`:
   *
   *   - Notice < 12h: pay the scheduled hours as if worked (apply normal
   *     SCHADS rate matrix to `startTime` → `endTime`). Set
   *     `breakdown.cancelledButPaid = true` + `clauseRef = 'SCHADS cl 25.5(f)'`.
   *
   *   - Notice ≥ 12h: zero pay (worker didn't work; cancellation is
   *     informational). Set `breakdown.cancelledButPaid = false` for
   *     audit visibility.
   *
   * The "alternative shift offered" leg of cl 25.5(f) (which lets the
   * employer escape payable cancellation by offering a replacement) is
   * OUTSIDE engine scope — that's a coordinator workflow tracked
   * separately (Phase 4: SCHADS cl 25.5(f) alternative-shift workflow).
   *
   * Caller is responsible for passing `startTime` / `endTime` as the
   * SCHEDULED times (not actual worked times — there are none, the shift
   * was cancelled).
   */
  cancellation?: {
    cancelledAt: Date;
    scheduledStart: Date;
    reason?: string;
  };

  /**
   * Effective-dated Medea Nurses EA cl 18.4 postgraduate qualification
   * entitlement. The payroll layer resolves this from the governed staff
   * assignment; the Nurses engine validates the exact tier/percentage pair
   * and emits the allowance for ordinary work, overtime and annual leave.
   * Other award engines ignore this optional input.
   */
  postgraduateQualificationAllowance?: {
    assignmentId: string;
    componentCode:
      | 'NEA_POSTGRAD_CERTIFICATE_ALLOWANCE'
      | 'NEA_POSTGRAD_DEGREE_ALLOWANCE'
      | 'NEA_POSTGRAD_MASTERS_DOCTORATE_ALLOWANCE';
    qualificationTier:
      | 'POSTGRAD_HOSPITAL_OR_CERTIFICATE'
      | 'POSTGRAD_DIPLOMA_OR_NON_NURSING_DEGREE'
      | 'MASTERS_OR_DOCTORATE';
    percentage: 4 | 6.5 | 7.5;
    evidenceRef: string;
  } | null;

  /**
   * Phase 3 PR #3 sub-phase 3G (2026-05-18) — segment kind discriminant.
   *
   * Identifies whether this segment is regular work or a leave segment.
   * The engines use `segmentKind === 'paid_annual_leave'` (combined with
   * non-casual `employmentType`) to gate the LEAVE_LOADING_ALLOWANCE
   * emission per:
   *   - SCHADS cl 31.4(a) (specialist-verified; pre-fix cited cl 30.4(a) which does not exist as the SCHADS annual leave loading clause)
   *   - Aged Care cl 28.4(a) (specialist-verified 2026-05-19; pre-fix cited cl 30.4 which is Personal/carer's leave)
   *   - Nurses cl 22.5(a) (specialist-verified 2026-05-19 Codex Pass A retrospective P2-N; pre-fix cited cl 33.4(a) which does not exist as annual leave loading in MA000034)
   *   - HPSS cl 27.3(a) (primary-source verified 2026-09-14 — Codex Pass A retrospective challenged with cl 27.3; primary-source verification deferred until FWC consolidated MA000027 PDF read directly — FWC MA000027 clause 27.3 verification (2026-09-14) at packages/utils/src/award-engines/hpss.ts)
   *
   * Defaults to `'work'` so all pre-3G callers continue to behave
   * identically. Callers that compute leave segments (timesheet recompute,
   * pay-run snapshot, cumulative-hours, preflight, roster cost estimate)
   * set this explicitly per segment.
   *
   * Flat 17.5% × ordinary leave pay only. Shiftworker higher-of loading
   * (AC cl 28.4(b) / SCHADS cl 31.4(b) / Nurses cl 22.5(b)) is applied by
   * the pay-run leave-snapshot layer from rostered shift evidence.
   */
  segmentKind?:
    | 'work'
    | 'paid_annual_leave'
    | 'paid_personal_leave'
    | 'public_holiday_leave'
    | 'unpaid_leave'
    | 'long_service_leave';

  /**
   * Optional certification-gate inputs. When omitted, legacy callers keep the
   * previous allowance behavior. When supplied, cert-dependent allowances are
   * evaluated against the resolved registry and current verified worker certs.
   */
  activeCertifications?: readonly ActiveCertificationInput[];
  certGateRegistry?: CertGateRegistry;
  certGateAsOfDate?: string;
}

/**
 * A single per-shift allowance line item — stable shape persisted to the
 * timesheets.allowances_breakdown JSONB column so the pay-summary UI and
 * exports can itemise each MA000100 allowance individually rather than a
 * single rolled-up allowancesPay number.
 *
 * @property code — canonical identifier used by UI/reporting ("SLEEPOVER",
 *   "BROKEN_SHIFT", "TRAVEL_KM", "ON_CALL_WEEKDAY", "ON_CALL_WEEKEND",
 *   "DELAYED_MEAL", "FIRST_AID", "UNIFORM", "LAUNDRY").
 * @property clauseRef — MA000100 clause citation (e.g. "cl 20.6").
 * @property description — human label, may include computed basis ("2.5 km × $0.99").
 * @property amount — dollar amount (2 dp).
 * @property perShift — true for flat per-shift allowances that are subject
 *   to a weekly cap at pay-run aggregation (uniform, laundry, FT first-aid).
 */
/**
 * `code` is a string (not a closed union) so each award engine can emit its
 * own canonical allowance codes. SCHADS engine emits the SCHADS canonical
 * set ('SLEEPOVER', 'BROKEN_SHIFT', 'TRAVEL_KM', 'ON_CALL_WEEKDAY',
 * 'ON_CALL_WEEKEND', 'DELAYED_MEAL', 'FIRST_AID', 'UNIFORM', 'LAUNDRY');
 * Nurses MA000034 emits 'KM', 'ON_CALL_SATURDAY', 'ON_CALL_SUNDAY_PH',
 * 'MEAL', 'UNIFORM_PER_SHIFT', etc. Downstream callers (UI, exports) treat
 * these as opaque labels — the codes match the seed migration's
 * `award_allowance_lines.allowance_code` column.
 *
 * `clauseRef` is nullable: not every allowance line in every award has a
 * single canonical clause reference (eg some EBA-derived allowances).
 */
export interface ShiftPayAllowanceLine {
  code: string;
  clauseRef?: string | null;
  description: string;
  amount: number;
  perShift?: boolean;
  units?: number;
  unitType?: 'hours' | 'count' | 'km' | 'days';
  componentCode?: string;
  xeroEarningsCategory?: string;
  taxable?: boolean;
  superable?: boolean;
  /** STP Phase 2 allowance type code (e.g. 'CD' = cents per km). Set on the vehicle-km split lines. */
  stpCategory?: string;
  /** Resolved catalogue floor; applied only once across a worker's local week. */
  weeklyMinimum?: { amount: number; scopeKey: string; appliedTopUpAmount?: number };
  providerManualAllowance?: boolean;
  manualCarryForward?: boolean;
  manualCarryForwardSource?: string;
  manualCarryForwardLineIndex?: number;
}

export interface ShiftPayCertGateResult {
  awardCode: string;
  allowanceCode: string;
  certificationType: string;
  requirementCode: string;
  requirementLabel: string;
  required: boolean;
  asOfDate: string;
  status: 'SATISFIED' | 'SUPPRESSED';
}

/**
 * Phase 1D (Award Engine Integrity 2026-05) — classification snapshot used
 * on `ShiftPayBreakdown.actingClassification` / `substantiveClassification`
 * when a higher-duties override applied. Both fields are populated together
 * (acting = the role the engine actually paid at; substantive = the worker's
 * underlying role) or both null when no override applied.
 *
 * `classificationStream` is null for legacy SCHADS callers that pass a flat
 * `schadsLevel` string without an explicit stream concept (SCHADS uses flat
 * classifications, not stream + level + payPoint).
 */
export interface ShiftClassificationSnapshot {
  classificationStream: string | null;
  classificationLevel: string;
  payPoint: string | null;
}

export interface ShiftPayCoverageGap {
  band: string;
  allowanceCode?: string;
  blockCode?: string;
  blockKind?: string;
  employment: string;
  reason: string;
}

export interface ShiftPayBreakdown {
  /** The normalized level used; null if input couldn't be recognized */
  normalizedLevel: NormalizedSchadsLevel | null;
  /** Base hourly rate from the SCHADS rate table */
  baseHourlyRate: number;
  /** Total paid hours after break deduction */
  hoursWorked: number;
  /** Time-band breakdowns in hours */
  bands: {
    weekdayOrdinary: number;
    saturdayOrdinary: number;
    sundayOrdinary: number;
    publicHoliday: number;
    eveningShift: number;
    nightShift: number;
  };
  /** Dollar components */
  basePay: number;
  penaltyPay: number;
  shiftLoadingPay: number;
  /** SCHADS settlement-only cumulative flank accounting; absent for other engines. */
  ordinaryHoursBySleepoverPortion?: { before: number; after: number };
  overtimeHours: number;
  overtimePay: number;
  /** Overtime hours paid at 1.5x. */
  overtimeFirstHours: number;
  /** Overtime hours paid at 2.0x (extended tier, and all Sunday overtime). */
  overtimeRemainderHours: number;
  /** Overtime hours paid at 2.5x (public holiday). No earnings-rate mapping yet. */
  overtimePublicHolidayHours: number;
  allowancesPay: number;
  /** Itemised allowance lines that sum to allowancesPay. */
  allowanceLines: ShiftPayAllowanceLine[];
  /** Certification-gate decisions used by cert-dependent allowance lines. */
  certGateResults?: ShiftPayCertGateResult[];
  /** Caller-side automatic top-ups applied after engine calculation. */
  automaticAwardTopUps?: {
    version: string;
    applied: Array<{
      code: string;
      clauseRef: string;
      amount: number;
      units: number;
    }>;
  };
  /** Annual leave higher-of comparison applied by pay-run leave snapshot. */
  annualLeaveLoadingHigherOf?: {
    flatLoadingAmount: number;
    rosteredPenaltyEquivalent: number;
    appliedLoadingAmount: number;
    method: string;
    rosteredHoursCompared: number;
    rosterShiftIds: string[];
  };
  /** EBA strict-mode gaps that make this breakdown non-certifiable. */
  coverageGaps?: ShiftPayCoverageGap[];
  grossTotal: number;
  /** Warnings (e.g., "level could not be recognised — using fallback rate") */
  warnings: string[];
  /**
   * Phase 1D (Award Engine Integrity 2026-05) — captured when a higher-duties
   * override applied at calculation time. `actingClassification` is the role
   * the engine actually paid at (i.e. the elevated role); `substantiveClassification`
   * is the worker's underlying substantive role. Both null when no override
   * applied OR when the override resolved to the same role as substantive.
   *
   * Consumers:
   *   - `/payroll/queue` "Shifts paid at acting rates this period" report
   *     reads pay_run_items.breakdown_snapshot.actingClassification IS NOT NULL.
   *   - Audit pack exports the same as evidence of acting-rate payments.
   *   - Care-minutes attribution module (decision pending; see CLAUDE.md):
   *     whether an acting RN counts toward 44 RN min/resident/day.
   *
   * Optional for backwards compatibility with the existing 3 prod rows
   * which predate Phase 1D.
   */
  actingClassification?: ShiftClassificationSnapshot | null;
  substantiveClassification?: ShiftClassificationSnapshot | null;
  /**
   * Phase W4.0.5 (Award Engine Integrity 2026-05-17) — Aged Care MA000018
   * cl 15.3 leading-hand "all-purpose" base-rate lift evidence. Populated
   * only when the engine lifts `baseHourlyRate` for a designated leading-
   * hand worker on this shift (per `leadingHandSupervisedCount >= 2` input).
   *
   * The lift is INVISIBLE in `allowanceLines` because cl 15.3(c) says the
   * allowance "forms part of salary for all purposes" — it propagates
   * through every penalty/OT/shift-loading multiplier as part of the base
   * rate, NOT as a separate allowance line. This field exposes the lift
   * for audit-pack visibility + downstream consumers (W4.1 coordinator UI
   * reconciliation, future ATO STP reporting, FW Act "reasonable steps"
   * defence evidence).
   *
   * `null` when no lift applied (worker not in charge OR count < 2 OR no
   * matching DB allowance row).
   *
   * Consumers:
   *   - `compliance-audit-pack.ts` emits a CSV of shifts with leading-hand
   *     lift evidence per period.
   *   - Future: payroll-reconciliation harness uses `liftPerHour` to
   *     reproduce the engine's lifted-rate math under replay.
   *
   * Engine-specific: only the Aged Care engine populates this field;
   * Nurses/HPSS/SCHADS leave it null (cl 15.3 has no equivalent in
   * MA000034/MA000027/MA000100; verified W4.0.5 specialist 2026-05-17).
   */
  leadingHandAllPurposeLift?: {
    /** Tier code from `AGED_CARE_LEADING_HAND_EMISSION.tiers` (e.g., 'LEADING_HAND_6_10'). */
    tierAllowanceCode: string;
    /** Number of other employees supervised on this shift (≥2 per cl 15.3(a)). */
    supervisedCount: number;
    /** Per-week amount in $ from DB allowance row (e.g., 45.68 for tier 6-10). */
    weeklyAmount: number;
    /** Per-hour lift = weeklyAmount / 38 ordinary hours, full precision (not rounded). */
    liftPerHour: number;
  } | null;
  /**
   * Medea Nurses EA 2024 cls 18.4-18.6 postgraduate qualification
   * allowance evidence. The allowance remains separately itemised for Xero,
   * while overtime and annual-leave calculations include the same percentage.
   */
  postgraduateQualificationAllowance?: {
    assignmentId: string;
    componentCode: string;
    qualificationTier: string;
    percentage: number;
    relevantHourlyRate: number;
    allowanceHourlyRate: number;
    ordinaryHours: number;
    overtimeHours: number;
    ordinaryAmount: number;
    overtimeAmount: number;
    annualLeaveLoadingAmount: number;
    totalAmount: number;
    evidenceRef: string;
  } | null;
  /**
   * Phase 1H (Award Engine Integrity 2026-05) — SCHADS cl 25.5(f) home-care
   * cancellation flag + audit trail.
   *
   *   - `cancelledButPaid = true`: the cancellation triggered payable
   *     treatment (notice < 12h before scheduled start). The pay values
   *     in this breakdown represent the scheduled hours at the scheduled
   *     rate matrix — paid even though the worker did not perform the work.
   *
   *   - `cancelledButPaid = false`: cancellation was logged but notice
   *     was ≥ 12h, so no special pay treatment applies. Pay values are
   *     zero (worker didn't work and isn't being paid).
   *
   *   - `null` / undefined: no cancellation context (the normal worked-shift
   *     path; existing behaviour unchanged).
   *
   * `cancellationNoticeHours` is the computed notice period
   * (scheduledStart − cancelledAt) in hours, surfaced for audit pack +
   * coordinator visibility. Useful for spotting near-boundary cases
   * (e.g. 12.05h notice → not payable, 11.95h notice → payable).
   */
  cancelledButPaid?: boolean | null;
  cancellationNoticeHours?: number | null;
}

export interface RosterComplianceIssue {
  code:
    | 'MIN_ENGAGEMENT'
    | 'MAX_DAILY_HOURS'
    | 'SPREAD_OF_HOURS'
    | 'MIN_BREAK_BETWEEN_SHIFTS'
    | 'MAX_CONSECUTIVE_DAYS';
  message: string;
  shiftId?: string;
}

export interface ShiftPayWithOvertimeInput extends ShiftPayInput {
  /** Approved matching-source fact only; interval offsets in minutes from overtime start. */
  nursesOvertimeException?: {
    approved: true;
    startMinute: number;
    endMinute: number;
    reason: string;
  } | null;
  /** Ordinary hours already worked earlier in the award week before this shift. */
  ordinaryHoursWorkedEarlierThisWeek?: number;
  /** Default 38 ordinary hours/week. */
  ordinaryHoursLimitPerWeek?: number;
  /** Ordinary hours already worked earlier in the configured ordinary-hours cycle. */
  ordinaryHoursWorkedEarlierThisCycle?: number;
  /** Configured ordinary-hours cap for the cycle, e.g. 76h/14d or 88h/14d. */
  ordinaryHoursLimitPerCycle?: number;
  /** Configured ordinary-hours cycle length. Carried for audit/replay context. */
  ordinaryHoursCycleDays?: number;
  /**
   * Use when a provider EA defines ordinary hours over a cycle longer than
   * seven days. The cycle cap then controls cumulative OT rather than a
   * synthetic weekly equivalent.
   */
  disableWeeklyOrdinaryHoursLimit?: boolean;
  /**
   * Effective, independently anchored ordinary-hours windows resolved by the
   * payroll snapshot caller. These immutable windows are additional concurrent
   * limits: they never displace another evidence-bound worker/agreement window
   * or the configured cycle limit. When the scalar weekly fallback is disabled,
   * an applicable employee-specific weekly ceiling must be represented here.
   */
  ordinaryHoursCapWindows?: ResolvedOrdinaryHoursCapWindow[];
  /** Default 10 ordinary hours per shift/day. */
  ordinaryHoursLimitPerShift?: number;
  /**
   * Ordinary hours already worked earlier in this shift before the priced
   * fragment. Settlement segmented pricing threads this so per-shift caps stay
   * coherent across noncontiguous WORK intervals.
   */
  ordinaryHoursWorkedEarlierThisShift?: number;
  /**
   * Overtime hours already worked earlier in this shift before the priced
   * fragment. SCHADS consumes this as the OT sequence counter for tier cutover.
   */
  overtimeHoursWorkedEarlierThisShift?: number;
  /**
   * Initial-tier overtime hours already consumed earlier in this shift before
   * the priced fragment. Nurses, Aged Care and HPSS use this for the shared
   * first-2h 150% budget; SCHADS ignores it in favour of
   * `overtimeHoursWorkedEarlierThisShift`.
   */
  overtimeInitialTierHoursConsumedEarlierThisShift?: number;
  /**
   * Full-time employees get the first 3 overtime hours at 1.5x on Monday to
   * Saturday; part-time and casual employees get the first 2.
   */
  isFullTimeForOvertime?: boolean;
}

/** OT rates use the actual calendar tail; substitution removes what ordinary pricing paid. */
export function ordinaryOvertimeBackoutHours(
  bands: ShiftPayBreakdown['bands'],
  actual: { weekday: number; saturday: number; sunday: number; publicHoliday: number },
  majorityPricing: boolean,
): typeof actual {
  if (!majorityPricing) return actual;
  const hours = actual.weekday + actual.saturday + actual.sunday + actual.publicHoliday;
  return {
    weekday: bands.weekdayOrdinary > 0 ? hours : 0,
    saturday: bands.saturdayOrdinary > 0 ? hours : 0,
    sunday: bands.sundayOrdinary > 0 ? hours : 0,
    publicHoliday: bands.publicHoliday > 0 ? hours : 0,
  };
}

export function subtractOvertimeFromOrdinaryBands(
  bands: ShiftPayBreakdown['bands'],
  overtimeHoursByDayType: {
    weekday?: number;
    saturday?: number;
    sunday?: number;
    publicHoliday?: number;
  },
): ShiftPayBreakdown['bands'] {
  const next: ShiftPayBreakdown['bands'] = { ...bands };
  const normalizedHours = (hours: number | undefined): number =>
    typeof hours === 'number' && Number.isFinite(hours) ? Math.max(0, hours) : 0;
  const subtract = (keys: Array<keyof ShiftPayBreakdown['bands']>, hours: number) => {
    let remaining = normalizedHours(hours);
    for (const key of keys) {
      if (remaining <= 0) break;
      const take = Math.min(next[key], remaining);
      next[key] = Math.round((next[key] - take) * 10000) / 10000;
      remaining = Math.round((remaining - take) * 10000) / 10000;
    }
  };

  subtract(['publicHoliday'], normalizedHours(overtimeHoursByDayType.publicHoliday));
  subtract(['sundayOrdinary'], normalizedHours(overtimeHoursByDayType.sunday));
  subtract(['saturdayOrdinary'], normalizedHours(overtimeHoursByDayType.saturday));

  const weekdayOvertime = normalizedHours(overtimeHoursByDayType.weekday);
  // Weekday shift-loading bands are overlays on top of weekday ordinary
  // hours, not alternatives. OT substitution must remove the base ordinary
  // hours and the overlapping loading overlay hours from the auditable band
  // snapshot.
  subtract(['weekdayOrdinary'], weekdayOvertime);
  subtract(['nightShift', 'eveningShift'], weekdayOvertime);

  return next;
}

function finitePositiveNumber(value: number | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

function finiteNonNegativeNumber(value: number | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}

export function ordinaryHoursAccrualHeadroom(
  input: ShiftPayWithOvertimeInput,
  ordinaryHoursThisShift: number,
  defaultWeeklyLimit = 38,
): number {
  const accruedThisShift = Math.max(0, ordinaryHoursThisShift);
  const priorWeekOrdinary = Math.max(0, input.ordinaryHoursWorkedEarlierThisWeek ?? 0);
  const priorCycleOrdinary = Math.max(0, input.ordinaryHoursWorkedEarlierThisCycle ?? 0);
  const configuredWeeklyLimit = finitePositiveNumber(input.ordinaryHoursLimitPerWeek);
  const weeklyLimit = configuredWeeklyLimit ?? defaultWeeklyLimit;
  const cycleLimit = finitePositiveNumber(input.ordinaryHoursLimitPerCycle);
  // A long-cycle profile suppresses the scalar weekly limit. That scalar does
  // not carry enough provenance to distinguish a resolver-supplied synthetic
  // 38-hour fallback from an independently approved staff limit. Any genuine
  // concurrent staff/contract weekly ceiling must therefore arrive as an
  // evidence-bound ordinaryHoursCapWindow and remains applicable below.
  const weeklyHeadroom = input.disableWeeklyOrdinaryHoursLimit
    ? Number.POSITIVE_INFINITY
    : Math.max(0, weeklyLimit - priorWeekOrdinary - accruedThisShift);
  const cycleHeadroom =
    cycleLimit === null
      ? Number.POSITIVE_INFINITY
      : Math.max(0, cycleLimit - priorCycleOrdinary - accruedThisShift);

  const explicitWindowHeadroom: number[] = [];
  if (input.ordinaryHoursCapWindows !== undefined) {
    // Agreement cap sets and staff-specific approved-pattern sets retain their
    // own immutable identities. Validate each source set independently, then
    // apply every resolved window concurrently.
    const windowsByCapSetId = new Map<string, ResolvedOrdinaryHoursCapWindow[]>();
    for (const window of input.ordinaryHoursCapWindows) {
      const windows = windowsByCapSetId.get(window.capSetId) ?? [];
      windows.push(window);
      windowsByCapSetId.set(window.capSetId, windows);
    }
    for (const windows of windowsByCapSetId.values()) {
      validateResolvedOrdinaryHoursCapWindows(windows);
    }
    explicitWindowHeadroom.push(
      ...input.ordinaryHoursCapWindows.map((window) =>
        Math.max(0, window.maximumHours - window.ordinaryHoursWorkedBeforeShift - accruedThisShift),
      ),
    );
  }

  return Math.min(weeklyHeadroom, cycleHeadroom, ...explicitWindowHeadroom);
}

export function ordinaryHoursForShiftWithinConfiguredLimits(
  input: ShiftPayWithOvertimeInput,
  totalShiftHours: number,
  defaultDailyLimit: number,
  defaultWeeklyLimit = 38,
): number {
  const dailyLimit = finiteNonNegativeNumber(input.ordinaryHoursLimitPerShift) ?? defaultDailyLimit;
  const priorShiftOrdinary = Math.max(0, input.ordinaryHoursWorkedEarlierThisShift ?? 0);
  const remainingDailyHeadroom = Math.max(0, dailyLimit - priorShiftOrdinary);
  const dailyOrdinary = Math.min(Math.max(0, totalShiftHours), remainingDailyHeadroom);
  return Math.max(
    0,
    Math.min(
      dailyOrdinary,
      ordinaryHoursAccrualHeadroom(input, priorShiftOrdinary, defaultWeeklyLimit),
    ),
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Level normaliser
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Normalise a SCHADS level string from any of the common messy formats into
 * a canonical form matching one of the rate tables. Returns null if the input
 * cannot be recognised.
 *
 * Accepts:
 *   - Canonical SACS: 'L1.1', 'L3.3', 'L8.3' → pass through
 *   - Canonical home care aged: 'HCA1' – 'HCA6' → pass through
 *   - Canonical home care disability: 'HCD1', 'HCD2.1', 'HCD5.2' → pass through
 *   - Plain integer: '3' → 'L3.1' (assumes pay point 1)
 *   - LEVEL_N format: 'LEVEL_3' → 'L3.1'
 *   - Role-prefix format: 'CW_L1' (care worker) → 'HCA1'
 *                         'RN_L3' (registered nurse) → 'L6.1' (SACS L6 = RN tier)
 *                         'EN_L2' (enrolled nurse) → 'L4.1' (SACS L4 = EN tier)
 *                         'AD_L4' (admin) → 'L4.1'
 *                         'CO_L5' (coordinator) → 'L5.1'
 *                         'FO_L5' (finance officer) → 'L5.1'
 *                         'AH_L2' (allied health) → 'L4.1' (entry-level AH)
 *                         'AH_L3' → 'L5.1'
 */
export function normalizeSchadsLevel(
  raw: string | null | undefined,
  override?: SchadsRatesOverride,
): NormalizedSchadsLevel | null {
  if (!raw) return null;
  const trimmed = raw.trim().toUpperCase();
  if (!trimmed) return null;
  const rates = resolveRates(override);

  // Already canonical?
  if (trimmed in rates.levelRates) return trimmed as NormalizedSchadsLevel;
  if (trimmed in rates.homeCareAgedRates) return trimmed as NormalizedSchadsLevel;
  if (trimmed in rates.homeCareDisabilityRates) return trimmed as NormalizedSchadsLevel;

  // Plain integer: '3' → 'L3.1'
  if (/^\d+$/.test(trimmed)) {
    const candidate = `L${trimmed}.1`;
    if (candidate in rates.levelRates) return candidate as NormalizedSchadsLevel;
  }

  // LEVEL_N format: 'LEVEL_3' → 'L3.1'
  const levelMatch = trimmed.match(/^LEVEL[_ ]?(\d+)$/);
  if (levelMatch) {
    const candidate = `L${levelMatch[1]}.1`;
    if (candidate in rates.levelRates) return candidate as NormalizedSchadsLevel;
  }

  // Role-prefix format
  // CW (care worker) → HCA (home care aged care); maps CW_Lx → HCAx
  // For vedran's New Age HACS (home care provider), CW_L1-L6 means Home Care Aged Care L1-L6
  const cwMatch = trimmed.match(/^CW[_ ]?L?(\d)$/);
  if (cwMatch) {
    const candidate = `HCA${cwMatch[1]}`;
    if (candidate in rates.homeCareAgedRates) return candidate as NormalizedSchadsLevel;
  }

  // RN, EN, AD, CO, FO, AH → SACS levels
  //
  // ⚠️ DEPRECATION WARNING (2026-04-13): These role-prefix mappings approximate
  // non-SCHADS roles into SACS levels. This is legally insufficient:
  //   - RN/EN should be classified under Nurses Award 2020 (MA000034)
  //   - AH should be classified under HPSS Award 2020 (MA000027)
  //   - AD/CO/FO MAY be SCHADS SACS or may be Aged Care Award (MA000018)
  //
  // Staff with these prefixes should have their `award_code` set to the correct
  // instrument. The `needs_award_review` flag is set by the migration
  // 20260513000009_multi_award_instrument_model.sql for affected records.
  //
  // This mapping is retained ONLY for backward compatibility with existing
  // pay calculations. New code should use the `award_code` and
  // `classification_level` fields on the staff record directly.
  const roleMatch = trimmed.match(/^(RN|EN|AD|CO|FO|AH)[_ ]?L?(\d)$/);
  if (roleMatch && roleMatch[1] && roleMatch[2]) {
    const role = roleMatch[1];
    const num = roleMatch[2];
    const rolePrefixToLevel: Record<string, number> = {
      AD: 2,
      CO: 4,
      FO: 4,
      AH: 3,
      EN: 3,
      RN: 5,
    };
    const offset = rolePrefixToLevel[role] ?? 0;
    const targetLevel = offset + parseInt(num, 10);
    const candidate = `L${targetLevel}.1`;
    if (candidate in rates.levelRates) return candidate as NormalizedSchadsLevel;
  }

  return null;
}

/**
 * Look up the base hourly rate for a (possibly messy) SCHADS level string.
 * Pass the shift date and tenant timezone for historical/future calculations.
 * Without a date, uses the stable 2026-10-01 compatibility baseline.
 * Returns 0 if the level is unrecognizable — caller should check warnings.
 */
export function lookupSchadsRate(
  rawLevel: string | null | undefined,
  override?: SchadsRatesOverride,
  asAt?: Date,
  timezone: string = DEFAULT_TZ,
): number {
  const normalized = normalizeSchadsLevel(rawLevel, override);
  if (!normalized) return 0;
  const rates = resolveRates(override, asAt, timezone);
  return (
    rates.levelRates[normalized as keyof typeof rates.levelRates] ??
    rates.homeCareAgedRates[normalized as keyof typeof rates.homeCareAgedRates] ??
    rates.homeCareDisabilityRates[normalized as keyof typeof rates.homeCareDisabilityRates] ??
    0
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Shift segmentation (handles mixed day shifts)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Phase 1A.6: the engine-local `segmentShift` function and its tz/hour
 * helpers (`getDayOfWeekInTz`, `getHourInTz`) previously defined here
 * have been removed; SCHADS now consumes `classifyShift` from
 * `./award-engines/shift-classifier.ts` in `'PER_SEGMENT'` mode, which
 * retains per-day allocation independently of whole-shift loading.
 *
 * The local `ShiftSegment` interface is retained because downstream
 * helpers reference it as a type. Its shape matches the shared
 * classifier's `ShiftSegment` (modulo property order, which is
 * structurally irrelevant for object literals).
 */
interface ShiftSegment {
  dayType: 'weekday' | 'saturday' | 'sunday' | 'publicHoliday';
  hour: number;
  hours: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// Main pay calculator
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Compute gross pay for a single shift per SCHADS Award.
 *
 * This function is a pure calculator — it takes shift data + worker
 * classification and returns a dollar breakdown. It does NOT know about:
 *  - Other shifts the worker has done this week (no cross-shift overtime)
 *  - Daily 38h cap across multiple shifts
 *  - Whether a public holiday applies (caller must pass isPublicHoliday)
 *
 * For weekly overtime + PH detection, the server-action layer should compute
 * the extra information and pass it in.
 */
/**
 * Phase 1H — SCHADS cl 25.5(f) cancellation evaluation. Returns null when no
 * cancellation context provided; otherwise returns `{ payable, noticeHours }`
 * where:
 *   - `payable=true` when the cancellation occurred < 12h before scheduled
 *     start. The caller should pay scheduled hours as if worked.
 *   - `payable=false` when notice was ≥ 12h. Cancellation is informational;
 *     no special pay treatment.
 */
function evaluateCancellation(
  cancellation: ShiftPayInput['cancellation'],
): { payable: boolean; noticeHours: number } | null {
  if (!cancellation) return null;

  const noticeMs = cancellation.scheduledStart.getTime() - cancellation.cancelledAt.getTime();

  const noticeHours = Math.max(0, noticeMs / (1000 * 60 * 60));
  // Strict < 12h: at exactly 12h, cancellation is NOT payable (the 12h
  // threshold is the boundary; only inside it triggers cl 25.5(f)).
  return { payable: noticeHours < 12, noticeHours };
}

export function calculateShiftPay(
  input: ShiftPayInput,
  override?: SchadsRatesOverride,
): ShiftPayBreakdown {
  const warnings: string[] = [];
  const rates = resolveRates(override, input.startTime, input.timezone ?? DEFAULT_TZ);

  // 1. Resolve base rate
  const baseHourlyRate = lookupSchadsRate(
    input.schadsLevel,
    override,
    input.startTime,
    input.timezone ?? DEFAULT_TZ,
  );
  const normalizedLevel = normalizeSchadsLevel(input.schadsLevel, override);
  if (baseHourlyRate === 0) {
    warnings.push(
      `SCHADS level "${input.schadsLevel}" not recognised — gross pay will be $0. Set the worker's schads_level to a canonical value (e.g., "L3.1" for SACS or "HCA3" for home care aged care).`,
    );
  }

  // Phase 1H (SCHADS cl 25.5(f)) — evaluate cancellation context BEFORE pay
  // computation. When the cancellation occurred ≥ 12h before scheduled
  // start, return zero pay tagged as informational. When < 12h, continue
  // the normal calc — caller will have passed scheduledStart/scheduledEnd
  // as input.startTime/input.endTime so the standard pay matrix applies.
  const cancellationEval = evaluateCancellation(input.cancellation);
  if (cancellationEval && !cancellationEval.payable) {
    warnings.push(
      `SCHADS cl 25.5(f) — cancellation notice ${cancellationEval.noticeHours.toFixed(2)}h ≥ 12h threshold; no payable-cancellation treatment.`,
    );
    return {
      ...zeroBreakdown(normalizedLevel, baseHourlyRate, warnings),
      cancelledButPaid: false,
      cancellationNoticeHours: Math.round(cancellationEval.noticeHours * 100) / 100,
    };
  }

  if (isSchadsRemoteWork(input)) {
    return calculateSchadsRemoteWorkPay({
      input,
      baseHourlyRate,
      normalizedLevel,
      warnings,
    });
  }

  // 2. Compute total hours
  const totalMs = input.endTime.getTime() - input.startTime.getTime();
  if (totalMs <= 0) {
    warnings.push('Shift end must be after start — returning zero pay.');
    return zeroBreakdown(normalizedLevel, baseHourlyRate, warnings);
  }
  // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
  const rawHours = totalMs / (1000 * 60 * 60);
  const breakHours =
    payrollBreakMinutesForEngine(
      input.breakMinutes ?? 0,
      input.breakTreatment,
      input.breakSemantics,
    ) / 60;

  // 3. Choose penalty rate set
  const penaltyRates =
    input.employmentType === 'casual' ? rates.casualPenaltyRates : rates.permanentPenaltyRates;
  const tz = input.timezone ?? 'Australia/Sydney';
  const category = input.employeeCategory ?? 'dayworker';
  const casualMultiplier = input.employmentType === 'casual' ? 1.25 : 1.0;

  // 4. Allocate weekend / public-holiday hours per segment.
  const classified = classifyShift({
    startTime: input.startTime,
    endTime: input.endTime,
    breakMinutes: 0, // SCHADS applies break weighting downstream via segmentWeight
    timezone: tz,
    isPublicHoliday: input.isPublicHoliday ?? false,
    publicHolidaySegments: input.publicHolidaySegments,
    shiftCharacterRules: SCHADS_SHIFT_RULES,
    dominantDayRule: 'PER_SEGMENT',
  }).segments;
  const sleepoverWindow = resolvedSleepoverWindow(
    input.shiftLoadingContext?.hasSleepover ?? input.hasSleepover,
    input.sleepoverStart,
    input.sleepoverEnd,
    input.shiftEnvelopeStartTime ?? input.startTime,
    tz,
  );
  const segments = workSegmentsOutsideSleepover(input.startTime, classified, sleepoverWindow);
  const loadingCharacters = schadsShiftLoadingCharacters(input);
  const workRawHours = sleepoverWindow
    ? segments.reduce((sum, seg) => sum + seg.hours, 0)
    : rawHours;
  const hoursWorked = Math.max(0, workRawHours - breakHours);
  // Weighted by "paid" hours (remove unpaid break proportionally from each segment)
  const segmentWeight = workRawHours > 0 ? hoursWorked / workRawHours : 0;

  // 5. Aggregate into bands
  const bands = {
    weekdayOrdinary: 0,
    saturdayOrdinary: 0,
    sundayOrdinary: 0,
    publicHoliday: 0,
    eveningShift: 0,
    nightShift: 0,
  };

  for (const seg of segments) {
    const hours = seg.hours * segmentWeight;
    // Day-type aggregation
    if (seg.dayType === 'publicHoliday') {
      bands.publicHoliday += hours;
    } else if (seg.dayType === 'saturday') {
      bands.saturdayOrdinary += hours;
    } else if (seg.dayType === 'sunday') {
      bands.sundayOrdinary += hours;
    } else {
      bands.weekdayOrdinary += hours;
    }
    // The whole work portion carries its loading on weekday ordinary hours.
    if (category === 'shiftworker' && seg.dayType === 'weekday') {
      if (loadingCharacters[seg.portion] === 'NIGHT') {
        bands.nightShift += hours;
      } else if (loadingCharacters[seg.portion] === 'AFTERNOON') {
        bands.eveningShift += hours;
      }
    }
  }

  // 6. Compute dollar amounts
  // Base pay = all hours × base rate × casual loading (if casual)
  // BUT: for casual, the casual penalty rates ALREADY include the 25% loading,
  // so we should NOT double-apply it on Sat/Sun/PH hours.
  // Structure: weekdayOrdinary × base × casualMult for casual weekday
  //            (Sat/Sun/PH) × base × casualPenalty for casual non-weekday
  //            (shift loading) × base × (casualPenalty - 1.0) as additional loading
  //
  // To keep it simple and auditable, compute as:
  //   basePay   = hoursWorked × baseRate × casualMultiplier
  //   penaltyPay = extra above base for Sat/Sun/PH
  //   shiftLoadingPay = extra for evening/night (shiftworker only)

  // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
  const basePay = hoursWorked * baseHourlyRate * casualMultiplier;

  // Penalty pay is the delta between the penalty rate and the casual-adjusted base
  const satMultiplier =
    input.employmentType === 'casual'
      ? penaltyRates.saturdayOrdinary // casual Sat = 1.75 (includes 25% loading)
      : penaltyRates.saturdayOrdinary; // perm Sat = 1.5
  const sunMultiplier = penaltyRates.sundayOrdinary; // perm 2.0, casual 2.25
  const phMultiplier = penaltyRates.publicHoliday; // perm 2.5, casual 2.75

  // Penalty pay = (band hours × base × penalty multiplier) − (band hours × base × casualMult)
  const penaltyPay =
    // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
    bands.saturdayOrdinary * baseHourlyRate * (satMultiplier - casualMultiplier) +
    // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
    bands.sundayOrdinary * baseHourlyRate * (sunMultiplier - casualMultiplier) +
    // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
    bands.publicHoliday * baseHourlyRate * (phMultiplier - casualMultiplier);

  // Shift loading — weekday ordinary hours of the classified work portion only.
  const shiftLoadingPay =
    category === 'shiftworker'
      ? // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
        bands.eveningShift *
          baseHourlyRate *
          (penaltyRates.afternoonShiftLoading - casualMultiplier) +
        // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
        bands.nightShift * baseHourlyRate * (penaltyRates.nightAfter12am - casualMultiplier)
      : 0;

  // 7. Allowances
  const certGateResults: ShiftPayCertGateResult[] = [];
  const allowanceLines = buildAllowanceLines(
    input,
    hoursWorked,
    rates,
    baseHourlyRate,
    warnings,
    certGateResults,
  );
  const allowancesPay = allowanceLines.reduce((sum, line) => sum + line.amount, 0);

  const grossTotal = sumMoney(basePay, penaltyPay, shiftLoadingPay, allowancesPay);

  // Phase 1H tag: if cancellation present + payable (notice < 12h), the
  // breakdown represents scheduled-hours-paid-as-if-worked. Surface the
  // cl 25.5(f) audit trail on the breakdown. cancellationEval was null in the
  // no-cancellation case, so the fields stay undefined for normal shifts.
  if (cancellationEval && cancellationEval.payable) {
    warnings.push(
      `SCHADS cl 25.5(f) — cancellation notice ${cancellationEval.noticeHours.toFixed(2)}h < 12h; paying scheduled hours as if worked.`,
    );
  }

  return {
    normalizedLevel,
    baseHourlyRate,
    hoursWorked: round2(hoursWorked),
    bands: {
      weekdayOrdinary: round2(bands.weekdayOrdinary),
      saturdayOrdinary: round2(bands.saturdayOrdinary),
      sundayOrdinary: round2(bands.sundayOrdinary),
      publicHoliday: round2(bands.publicHoliday),
      eveningShift: round2(bands.eveningShift),
      nightShift: round2(bands.nightShift),
    },
    basePay: round2(basePay),
    penaltyPay: round2(penaltyPay),
    shiftLoadingPay: round2(shiftLoadingPay),
    overtimeHours: 0,
    overtimePay: 0,
    overtimeFirstHours: 0,
    overtimeRemainderHours: 0,
    overtimePublicHolidayHours: 0,
    allowancesPay: round2(allowancesPay),
    allowanceLines,
    certGateResults: certGateResults.length > 0 ? certGateResults : undefined,
    grossTotal,
    warnings,
    ...(cancellationEval
      ? {
          cancelledButPaid: cancellationEval.payable,
          cancellationNoticeHours: Math.round(cancellationEval.noticeHours * 100) / 100,
        }
      : {}),
  };
}

/**
 * Build the itemised allowance line-items for a shift. Kept pure and
 * input-driven so both calculateShiftPay and calculateShiftPayWithOvertime
 * return identical breakdowns — and so the JSONB written to
 * timesheets.allowances_breakdown is stable across flows.
 *
 * Phase 1 §4.3: takes resolved `rates` rather than reading constants directly,
 * so the calculator can be driven by an effective-dated rate version.
 */
function buildAllowanceLines(
  input: {
    startTime?: Date;
    timezone?: string;
    hasSleepover?: boolean;
    hasBrokenShift?: 0 | 1 | 2;
    travelKm?: number;
    vehicleKmTaxSplit?: boolean;
    onCallWeekdayDays?: number;
    onCallWeekendDays?: number;
    hasDelayedMealAllowance?: boolean;
    hasFirstAidResponsibility?: boolean;
    hasUniformAllowance?: boolean;
    hasLaundryAllowance?: boolean;
    isFullTime?: boolean;
    segmentKind?: ShiftPayInput['segmentKind'];
    employmentType?: SchadsEmploymentType;
    activeCertifications?: readonly ActiveCertificationInput[];
    certGateRegistry?: CertGateRegistry;
    certGateAsOfDate?: string;
  },
  hoursWorked: number,
  rates: ResolvedRates,
  baseHourlyRate?: number,
  warnings?: string[],
  certGateResults?: ShiftPayCertGateResult[],
): ShiftPayAllowanceLine[] {
  // Phase 1N (2026-05-17) — canonical emission codes per
  // `project_allowance_code_naming_taxonomy.md`. SCHADS broken-shift splits
  // into BROKEN_SHIFT_SINGLE_ALLOWANCE (cl 20.12(a)) vs
  // BROKEN_SHIFT_MULTIPLE_ALLOWANCE (cl 20.12(b)) — per award structure
  // confirmed by Phase 1N specialist call #1 (artifact:
  // docs/operations/clause-verifications/2026-05-17-aged-care-legislation-schads-broken-shift-clause-ref.md).
  // Aged Care MA000018 cl 22.7 is unified BROKEN_SHIFT_ALLOWANCE (single
  // canonical code emitted by AC engine).
  const lines: ShiftPayAllowanceLine[] = [];
  if (input.hasSleepover) {
    lines.push({
      code: 'SLEEPOVER_ALLOWANCE',
      clauseRef: 'cl 25.7(d)',
      description: 'Sleepover allowance',
      amount: round2(rates.allowances.sleepoverRate),
      perShift: true,
    });
  }
  if (input.hasBrokenShift === 1) {
    lines.push({
      code: 'BROKEN_SHIFT_SINGLE_ALLOWANCE',
      clauseRef: 'cl 20.12(a)',
      description: 'Broken shift (one break)',
      amount: round2(rates.brokenShiftFlat.oneBreak),
      perShift: true,
    });
  }
  if (input.hasBrokenShift === 2) {
    lines.push({
      code: 'BROKEN_SHIFT_MULTIPLE_ALLOWANCE',
      clauseRef: 'cl 20.12(b)',
      description: 'Broken shift (two breaks)',
      amount: round2(rates.brokenShiftFlat.twoOrMoreBreaks),
      perShift: true,
    });
  }
  if (input.travelKm && input.travelKm > 0) {
    const kmPaidRate = rates.allowances.travelPerKm;
    if (input.vehicleKmTaxSplit && kmPaidRate >= SCHADS_VEHICLE_AWARD_FLOOR_2026_27) {
      // STP Phase 2: split the paid cents-per-km allowance into a PAYGW-exempt
      // component (ATO rate) and a taxable residual. Both are STP type 'CD'.
      // The 5,000-km/FY exemption cap is applied later at the pay-run layer by
      // applyVehicleKmFyExemptionCap (this per-shift emission assumes headroom).
      lines.push(...splitVehicleKmLines(input.travelKm, kmPaidRate));
    } else {
      if (input.vehicleKmTaxSplit) {
        // Flag on but the resolved paid rate is below the award floor: the split
        // can't be produced (no valid exempt/taxable division). Emit the legacy
        // single line and surface a warning rather than throwing — a mid-pay-run
        // throw would abort the whole batch for what is a rate-config error. The
        // config surface that enables the flag must guarantee rate >= floor.
        warnings?.push(
          `vehicleKmTaxSplit enabled but the paid km rate $${kmPaidRate.toFixed(2)}/km is below the SCHADS cl 20.7(a) award floor $${SCHADS_VEHICLE_AWARD_FLOOR_2026_27.toFixed(2)}/km — emitted a single KM_ALLOWANCE line; configure the paid km rate at or above the floor to enable the tax split.`,
        );
      }
      lines.push({
        code: 'KM_ALLOWANCE',
        clauseRef: 'cl 20.7(a)',
        description: `Vehicle allowance (${input.travelKm.toFixed(1)} km × $${kmPaidRate.toFixed(2)}/km)`,
        // eslint-disable-next-line statura/no-floating-point-money -- canonical FWC pay guide calc
        amount: round2(input.travelKm * kmPaidRate),
        units: input.travelKm,
        unitType: 'km',
        perShift: false,
      });
    }
  }
  if (input.onCallWeekdayDays && input.onCallWeekdayDays > 0) {
    lines.push({
      code: 'ON_CALL_WEEKDAY_ALLOWANCE',
      clauseRef: 'cl 20.11(a)',
      description: `On-call weekday (${input.onCallWeekdayDays} day${input.onCallWeekdayDays === 1 ? '' : 's'})`,
      // eslint-disable-next-line statura/no-floating-point-money -- canonical FWC pay guide calc
      amount: round2(input.onCallWeekdayDays * rates.allowances.onCallWeekday),
      perShift: false,
    });
  }
  if (input.onCallWeekendDays && input.onCallWeekendDays > 0) {
    lines.push({
      code: 'ON_CALL_WEEKEND_ALLOWANCE',
      clauseRef: 'cl 20.11(b)',
      description: `On-call weekend/PH (${input.onCallWeekendDays} day${input.onCallWeekendDays === 1 ? '' : 's'})`,
      // eslint-disable-next-line statura/no-floating-point-money -- canonical FWC pay guide calc
      amount: round2(input.onCallWeekendDays * rates.allowances.onCallWeekend),
      perShift: false,
    });
  }
  if (input.hasDelayedMealAllowance) {
    lines.push({
      code: 'MEAL_ALLOWANCE',
      clauseRef: 'cl 20.5',
      description: 'Delayed meal allowance',
      amount: round2(rates.allowances.mealAllowance),
      perShift: true,
    });
  }
  if (input.hasFirstAidResponsibility) {
    const firstAidGate = evaluateCertGateForAllowance(input, 'FIRST_AID_ALLOWANCE');
    if (firstAidGate.result) {
      certGateResults?.push(firstAidGate.result);
    }
    if (!firstAidGate.allowed) {
      warnings?.push(
        `FIRST_AID_ALLOWANCE suppressed: no current verified ${firstAidGate.result?.certificationType ?? 'FIRST_AID'} certification was found for ${firstAidGate.result?.asOfDate ?? 'the shift date'}.`,
      );
    } else if (input.isFullTime === true) {
      lines.push({
        code: 'FIRST_AID_ALLOWANCE',
        clauseRef: 'cl 20.6(a)',
        description: 'First-aid kit-bearer (FT weekly ÷ 5)',
        // eslint-disable-next-line statura/no-floating-point-money -- canonical FWC pay guide calc
        amount: round2(rates.allowances.firstAidWeekly / 5),
        units: 1,
        unitType: 'count',
        perShift: true,
      });
    } else {
      lines.push({
        code: 'FIRST_AID_ALLOWANCE',
        clauseRef: 'cl 20.6(b)',
        description: `First-aid kit-bearer (${hoursWorked.toFixed(2)} h × $${rates.allowances.firstAidHourlyPartTime.toFixed(2)}/h; weekly cap applied at pay run)`,
        // eslint-disable-next-line statura/no-floating-point-money -- canonical FWC pay guide calc
        amount: round2(hoursWorked * rates.allowances.firstAidHourlyPartTime),
        units: 1,
        unitType: 'count',
        perShift: false,
      });
    }
  }
  if (input.hasUniformAllowance) {
    lines.push({
      code: 'UNIFORM_ALLOWANCE',
      clauseRef: 'cl 20.2(b)',
      description: 'Uniform allowance (weekly cap applied at pay run)',
      amount: round2(rates.allowances.uniformPerShift),
      perShift: true,
    });
  }
  if (input.hasLaundryAllowance) {
    lines.push({
      code: 'LAUNDRY_ALLOWANCE',
      clauseRef: 'cl 20.2(b)',
      description: 'Laundry allowance (weekly cap applied at pay run)',
      amount: round2(rates.allowances.laundryPerShift),
      perShift: true,
    });
  }
  // Phase 3 PR #3 sub-phase 3G (2026-05-18) — Annual leave loading per
  // MA000100 cl 31.4(a). Fires when segmentKind === 'paid_annual_leave' AND
  // employmentType is non-casual (NES s.86 + cl 31.4 — casuals receive 25%
  // loading on ordinary hours in lieu, not annual leave). Naive 17.5% ×
  // ordinary leave pay (hoursWorked × baseHourlyRate, un-penalty-loaded —
  // leave is paid at ordinary time per cl 31.4(a)).
  //
  // cl 31.4(b) higher-of test is applied by the pay-run leave-snapshot
  // layer from rostered shift evidence.
  if (
    input.segmentKind === 'paid_annual_leave' &&
    input.employmentType !== 'casual' &&
    baseHourlyRate !== undefined &&
    baseHourlyRate > 0
  ) {
    // eslint-disable-next-line statura/no-floating-point-money -- canonical FWC pay guide calc — leave loading is 17.5% × ordinary leave pay per cl 31.4(a)
    const ordinaryLeavePay = hoursWorked * baseHourlyRate;
    lines.push({
      code: 'LEAVE_LOADING_ALLOWANCE',
      clauseRef: 'cl 31.4(a)',
      description: 'Annual leave loading (17.5%)',
      amount: round2(ordinaryLeavePay * SCHADS_LEAVE.annualLeaveLoading),
      perShift: true,
    });
  }
  return lines;
}

function evaluateCertGateForAllowance(
  input: {
    startTime?: Date;
    timezone?: string;
    activeCertifications?: readonly ActiveCertificationInput[];
    certGateRegistry?: CertGateRegistry;
    certGateAsOfDate?: string;
  },
  allowanceCode: string,
): { allowed: boolean; result?: ShiftPayCertGateResult } {
  if (!input.certGateRegistry) return { allowed: true };

  const requirement = getCertGateRequirement(input.certGateRegistry, 'MA000100', allowanceCode);
  if (!requirement) return { allowed: true };

  const asOfDate =
    input.certGateAsOfDate ??
    (input.startTime ? dateOnlyInTz(input.startTime, input.timezone ?? DEFAULT_TZ) : null);
  if (!asOfDate) return { allowed: false };

  const allowed = isCertGateSatisfied(requirement, input.activeCertifications ?? [], asOfDate);
  return {
    allowed,
    result: {
      awardCode: requirement.awardCode,
      allowanceCode: requirement.allowanceCode,
      certificationType: requirement.certificationType,
      requirementCode: requirement.requirementCode,
      requirementLabel: requirement.requirementLabel,
      required: requirement.required,
      asOfDate,
      status: allowed ? 'SATISFIED' : 'SUPPRESSED',
    },
  };
}

/**
 * Compute gross pay for a single shift including daily span and weekly
 * overtime. This is the authoritative calculator for active timesheet/payroll
 * flows where the caller can provide prior ordinary hours for the week.
 */
export function calculateShiftPayWithOvertime(
  input: ShiftPayWithOvertimeInput,
  override?: SchadsRatesOverride,
): ShiftPayBreakdown {
  const warnings: string[] = [];
  const rates = resolveRates(override, input.startTime, input.timezone ?? DEFAULT_TZ);

  const baseHourlyRate = lookupSchadsRate(
    input.schadsLevel,
    override,
    input.startTime,
    input.timezone ?? DEFAULT_TZ,
  );
  const normalizedLevel = normalizeSchadsLevel(input.schadsLevel, override);
  if (baseHourlyRate === 0) {
    warnings.push(
      `SCHADS level "${input.schadsLevel}" not recognised — gross pay will be $0. Set the worker's schads_level to a canonical value (e.g., "L3.1" for SACS or "HCA3" for home care aged care).`,
    );
  }

  // Phase 1H — SCHADS cl 25.5(f) cancellation early-exit (same as calculateShiftPay
  // path). Cancelled shifts don't have worked hours, so the OT path shortcuts
  // to the standard cancellation tagging: payable → delegate to non-OT
  // calc for the scheduled hours; not-payable → zero breakdown tagged.
  const cancellationEval = evaluateCancellation(input.cancellation);
  if (cancellationEval && !cancellationEval.payable) {
    warnings.push(
      `SCHADS cl 25.5(f) — cancellation notice ${cancellationEval.noticeHours.toFixed(2)}h ≥ 12h threshold; no payable-cancellation treatment.`,
    );
    return {
      ...zeroBreakdown(normalizedLevel, baseHourlyRate, warnings),
      cancelledButPaid: false,
      cancellationNoticeHours: Math.round(cancellationEval.noticeHours * 100) / 100,
    };
  }
  if (cancellationEval && cancellationEval.payable) {
    // Delegate to calculateShiftPay — cancelled shift = no actually-worked
    // hours, so no OT path applies. The non-OT calc handles scheduled-hours
    // payable treatment + tags the breakdown.
    return calculateShiftPay(input, override);
  }

  if (isSchadsRemoteWork(input)) {
    return calculateSchadsRemoteWorkPay({
      input,
      baseHourlyRate,
      normalizedLevel,
      warnings,
    });
  }

  const totalMs = input.endTime.getTime() - input.startTime.getTime();
  if (totalMs <= 0) {
    warnings.push('Shift end must be after start — returning zero pay.');
    return zeroBreakdown(normalizedLevel, baseHourlyRate, warnings);
  }

  // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
  const rawHours = totalMs / (1000 * 60 * 60);
  const breakHours =
    payrollBreakMinutesForEngine(
      input.breakMinutes ?? 0,
      input.breakTreatment,
      input.breakSemantics,
    ) / 60;

  const tz = input.timezone ?? 'Australia/Sydney';
  const category = input.employeeCategory ?? 'dayworker';
  const penaltyRates =
    input.employmentType === 'casual' ? rates.casualPenaltyRates : rates.permanentPenaltyRates;
  const casualMultiplier = input.employmentType === 'casual' ? 1.25 : 1.0;
  const sleepoverWindow = resolvedSleepoverWindow(
    input.shiftLoadingContext?.hasSleepover ?? input.hasSleepover,
    input.sleepoverStart,
    input.sleepoverEnd,
    input.shiftEnvelopeStartTime ?? input.startTime,
    tz,
  );
  const classified = classifyShift({
    startTime: input.startTime,
    endTime: input.endTime,
    breakMinutes: 0,
    timezone: tz,
    isPublicHoliday: input.isPublicHoliday ?? false,
    publicHolidaySegments: input.publicHolidaySegments,
    shiftCharacterRules: SCHADS_SHIFT_RULES,
    dominantDayRule: 'PER_SEGMENT',
  }).segments;
  const workSegments = workSegmentsOutsideSleepover(input.startTime, classified, sleepoverWindow);
  const loadingCharacters = schadsShiftLoadingCharacters(input);
  const workRawHours = sleepoverWindow
    ? workSegments.reduce((sum, seg) => sum + seg.hours, 0)
    : rawHours;
  const hoursWorked = Math.max(0, workRawHours - breakHours);
  // Phase 1A.6: replaced engine-local `segmentShift` with shared
  // `classifyShift` in PER_SEGMENT mode (byte-equal preserving).
  const paidSegments = workSegments.map((seg) => ({
    ...seg,
    hours: workRawHours > 0 ? seg.hours * (hoursWorked / workRawHours) : 0,
  }));

  const pr798459TwelveHourWrap =
    Boolean(input.agreedTwelveHourSleepoverShift) &&
    Boolean(input.shiftLoadingContext?.hasSleepover ?? input.hasSleepover) &&
    isPr798459Operative(input.shiftEnvelopeStartTime ?? input.startTime, tz);
  const ordinaryHoursLimitPerShift =
    input.ordinaryHoursLimitPerShift ??
    (pr798459TwelveHourWrap
      ? PR798459_TWELVE_HOUR_ORDINARY_CAP
      : SCHADS_ROSTER_LIMITS.maxOrdinaryHoursPerDay);
  const initialOvertimeHours = input.isFullTimeForOvertime
    ? rates.overtimeRates.initialHoursFullTime
    : rates.overtimeRates.initialHoursPartTimeOrCasual;

  const bands = {
    weekdayOrdinary: 0,
    saturdayOrdinary: 0,
    sundayOrdinary: 0,
    publicHoliday: 0,
    eveningShift: 0,
    nightShift: 0,
  };

  let basePay = 0;
  let penaltyPay = 0;
  let shiftLoadingPay = 0;
  let overtimePay = 0;
  let overtimeHours = 0;
  let ordinaryHoursThisShift = Math.max(0, input.ordinaryHoursWorkedEarlierThisShift ?? 0);
  let overtimeHoursSequence = Math.max(0, input.overtimeHoursWorkedEarlierThisShift ?? 0);
  const overtimeTierHours: OvertimeTierHours = { initial: 0, extended: 0, publicHoliday: 0 };
  const ordinaryHoursByPortion = {
    before: Math.max(0, input.shiftLoadingContext?.ordinaryHoursBeforeSleepover ?? 0),
    after: Math.max(0, input.shiftLoadingContext?.ordinaryHoursAfterSleepover ?? 0),
    undivided: 0,
  };

  for (const seg of paidSegments) {
    let remaining = seg.hours;
    if (remaining <= 0) continue;

    const isDayworkerSpanOvertime =
      category === 'dayworker' && seg.dayType === 'weekday' && (seg.hour < 6 || seg.hour >= 20);

    if (isDayworkerSpanOvertime) {
      const { hours, pay, tiers } = calculateOvertimeForHours(
        remaining,
        seg.dayType,
        baseHourlyRate,
        overtimeHoursSequence,
        initialOvertimeHours,
        rates.overtimeRates,
      );
      overtimeHours += hours;
      overtimePay += pay;
      overtimeTierHours.initial += tiers.initial;
      overtimeTierHours.extended += tiers.extended;
      overtimeTierHours.publicHoliday += tiers.publicHoliday;
      overtimeHoursSequence += hours;
      continue;
    }

    while (remaining > 0) {
      const portionCap =
        pr798459TwelveHourWrap && sleepoverWindow != null && seg.portion !== 'undivided'
          ? PR798459_MAX_ORDINARY_EITHER_SIDE
          : Number.POSITIVE_INFINITY;
      const ordinaryPortionHeadroom = Math.max(0, portionCap - ordinaryHoursByPortion[seg.portion]);
      const ordinaryDailyHeadroom = Math.max(
        0,
        Math.min(ordinaryHoursLimitPerShift - ordinaryHoursThisShift, ordinaryPortionHeadroom),
      );
      const ordinaryHeadroom = Math.min(
        ordinaryDailyHeadroom,
        ordinaryHoursAccrualHeadroom(input, ordinaryHoursThisShift, 38),
      );

      if (ordinaryHeadroom <= 0) {
        const { hours, pay, tiers } = calculateOvertimeForHours(
          remaining,
          seg.dayType,
          baseHourlyRate,
          overtimeHoursSequence,
          initialOvertimeHours,
          rates.overtimeRates,
        );
        overtimeHours += hours;
        overtimePay += pay;
        overtimeTierHours.initial += tiers.initial;
        overtimeTierHours.extended += tiers.extended;
        overtimeTierHours.publicHoliday += tiers.publicHoliday;
        overtimeHoursSequence += hours;
        break;
      }

      const ordinaryChunk = Math.min(remaining, ordinaryHeadroom);
      const ordinaryMultiplier = getOrdinarySegmentMultiplier(
        seg,
        category,
        input.employmentType,
        penaltyRates,
        casualMultiplier,
        loadingCharacters[seg.portion],
      );
      // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
      const ordinaryChunkPay = ordinaryChunk * baseHourlyRate * ordinaryMultiplier;

      if (seg.dayType === 'weekday') {
        // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
        basePay += ordinaryChunk * baseHourlyRate * casualMultiplier;
        if (category === 'shiftworker' && loadingCharacters[seg.portion] === 'NIGHT') {
          bands.nightShift += ordinaryChunk;
          // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
          shiftLoadingPay += ordinaryChunkPay - ordinaryChunk * baseHourlyRate * casualMultiplier;
        } else if (category === 'shiftworker' && loadingCharacters[seg.portion] === 'AFTERNOON') {
          bands.eveningShift += ordinaryChunk;
          // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
          shiftLoadingPay += ordinaryChunkPay - ordinaryChunk * baseHourlyRate * casualMultiplier;
        } else {
          bands.weekdayOrdinary += ordinaryChunk;
        }
      } else if (seg.dayType === 'saturday') {
        bands.saturdayOrdinary += ordinaryChunk;
        // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
        basePay += ordinaryChunk * baseHourlyRate * casualMultiplier;
        // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
        penaltyPay += ordinaryChunkPay - ordinaryChunk * baseHourlyRate * casualMultiplier;
      } else if (seg.dayType === 'sunday') {
        bands.sundayOrdinary += ordinaryChunk;
        // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
        basePay += ordinaryChunk * baseHourlyRate * casualMultiplier;
        // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
        penaltyPay += ordinaryChunkPay - ordinaryChunk * baseHourlyRate * casualMultiplier;
      } else {
        bands.publicHoliday += ordinaryChunk;
        // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
        basePay += ordinaryChunk * baseHourlyRate * casualMultiplier;
        // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
        penaltyPay += ordinaryChunkPay - ordinaryChunk * baseHourlyRate * casualMultiplier;
      }

      ordinaryHoursThisShift += ordinaryChunk;
      ordinaryHoursByPortion[seg.portion] += ordinaryChunk;
      remaining = roundHours(remaining - ordinaryChunk);
    }
  }

  const certGateResults: ShiftPayCertGateResult[] = [];
  const allowanceLines = buildAllowanceLines(
    input,
    hoursWorked,
    rates,
    baseHourlyRate,
    warnings,
    certGateResults,
  );
  const allowancesPay = allowanceLines.reduce((sum, line) => sum + line.amount, 0);

  const grossTotal = sumMoney(basePay, penaltyPay, shiftLoadingPay, overtimePay, allowancesPay);

  return {
    normalizedLevel,
    baseHourlyRate,
    hoursWorked: round2(hoursWorked),
    bands: {
      weekdayOrdinary: round2(bands.weekdayOrdinary),
      saturdayOrdinary: round2(bands.saturdayOrdinary),
      sundayOrdinary: round2(bands.sundayOrdinary),
      publicHoliday: round2(bands.publicHoliday),
      eveningShift: round2(bands.eveningShift),
      nightShift: round2(bands.nightShift),
    },
    basePay: round2(basePay),
    penaltyPay: round2(penaltyPay),
    shiftLoadingPay: round2(shiftLoadingPay),
    ...(input.shiftLoadingContext?.hasSleepover
      ? {
          ordinaryHoursBySleepoverPortion: {
            before: ordinaryHoursByPortion.before,
            after: ordinaryHoursByPortion.after,
          },
        }
      : {}),
    overtimeHours: round2(overtimeHours),
    overtimePay: round2(overtimePay),
    // The component-line trigger reads these; without them it splits overtime
    // by hours position and prices 200% hours at the 150% rate.
    overtimeFirstHours: round2(overtimeTierHours.initial),
    overtimeRemainderHours: round2(overtimeTierHours.extended),
    overtimePublicHolidayHours: round2(overtimeTierHours.publicHoliday),
    allowancesPay: round2(allowancesPay),
    allowanceLines,
    certGateResults: certGateResults.length > 0 ? certGateResults : undefined,
    grossTotal,
    warnings,
  };
}

function zeroBreakdown(
  normalizedLevel: NormalizedSchadsLevel | null,
  baseHourlyRate: number,
  warnings: string[],
): ShiftPayBreakdown {
  return {
    normalizedLevel,
    baseHourlyRate,
    hoursWorked: 0,
    bands: {
      weekdayOrdinary: 0,
      saturdayOrdinary: 0,
      sundayOrdinary: 0,
      publicHoliday: 0,
      eveningShift: 0,
      nightShift: 0,
    },
    basePay: 0,
    penaltyPay: 0,
    shiftLoadingPay: 0,
    overtimeHours: 0,
    overtimePay: 0,
    overtimeFirstHours: 0,
    overtimeRemainderHours: 0,
    overtimePublicHolidayHours: 0,
    allowancesPay: 0,
    allowanceLines: [],
    grossTotal: 0,
    warnings,
  };
}

function round2(n: number): number {
  return roundMoney(n);
}

function roundHours(n: number): number {
  return Math.round(n * 10000) / 10000;
}

function getOrdinarySegmentMultiplier(
  seg: ShiftSegment,
  category: SchadsEmployeeCategory,
  employmentType: SchadsEmploymentType,
  penaltyRates:
    | WidenToNumber<typeof SCHADS_PERMANENT_PENALTY_RATES>
    | WidenToNumber<typeof SCHADS_CASUAL_PENALTY_RATES>,
  casualMultiplier: number,
  loadingCharacter: SchadsShiftLoadingCharacter,
): number {
  if (seg.dayType === 'publicHoliday') return penaltyRates.publicHoliday;
  if (seg.dayType === 'saturday') return penaltyRates.saturdayOrdinary;
  if (seg.dayType === 'sunday') return penaltyRates.sundayOrdinary;
  if (category === 'shiftworker') {
    if (loadingCharacter === 'NIGHT') return penaltyRates.nightAfter12am;
    if (loadingCharacter === 'AFTERNOON') return penaltyRates.afternoonShiftLoading;
  }
  return employmentType === 'casual' ? casualMultiplier : 1.0;
}

/**
 * Overtime hours split by the multiplier they were actually paid at.
 *
 * This function always knew the split; it just threw it away and returned
 * totals. Downstream, the component-line trigger then had to guess, and its
 * fallback splits by HOURS POSITION (first two hours = 150%). That is wrong
 * whenever the tier does not follow position — Sunday overtime is 200% from the
 * first minute — so 200% hours were being routed to the 150% earnings rate and
 * priced there. Measured on Medea pay run 69947580: 14 staff, $1,645.99.
 */
export interface OvertimeTierHours {
  /** Paid at initialMultiplier (1.5x). */
  initial: number;
  /** Paid at extendedMultiplier or sundayMultiplier (2.0x). */
  extended: number;
  /** Paid at publicHolidayMultiplier (2.5x). Has no earnings-rate mapping yet. */
  publicHoliday: number;
}

function calculateOvertimeForHours(
  hours: number,
  dayType: ShiftSegment['dayType'],
  baseHourlyRate: number,
  overtimeHoursAlreadyWorked: number,
  initialOvertimeHours: number,
  overtimeRates: WidenToNumber<typeof SCHADS_OVERTIME_RATES>,
): { hours: number; pay: number; tiers: OvertimeTierHours } {
  const noTiers: OvertimeTierHours = { initial: 0, extended: 0, publicHoliday: 0 };
  if (hours <= 0) return { hours: 0, pay: 0, tiers: noTiers };
  if (dayType === 'sunday') {
    return {
      hours,
      // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
      pay: hours * baseHourlyRate * overtimeRates.sundayMultiplier,
      // 2.0x from the first minute — never a position-based split.
      tiers: { ...noTiers, extended: hours },
    };
  }
  if (dayType === 'publicHoliday') {
    return {
      hours,
      // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
      pay: hours * baseHourlyRate * overtimeRates.publicHolidayMultiplier,
      // 2.5x. Kept separate so it can never be folded into the 2.0x bucket,
      // which would underpay by 20%.
      tiers: { ...noTiers, publicHoliday: hours },
    };
  }

  const initialHoursRemaining = Math.max(0, initialOvertimeHours - overtimeHoursAlreadyWorked);
  const initialHours = Math.min(hours, initialHoursRemaining);
  const extendedHours = Math.max(0, hours - initialHours);
  const pay =
    // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
    initialHours * baseHourlyRate * overtimeRates.initialMultiplier +
    // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
    extendedHours * baseHourlyRate * overtimeRates.extendedMultiplier;

  return {
    hours,
    pay,
    tiers: { initial: initialHours, extended: extendedHours, publicHoliday: 0 },
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Roster compliance validator
// ═══════════════════════════════════════════════════════════════════════════

export interface RosterShiftForCompliance {
  id?: string;
  startTime: Date;
  endTime: Date;
  breakMinutes?: number;
  /**
   * Home care vs residential, from the shift's own shape. Supplied so the
   * minimum-break check can apply cl 25.6 rather than cl 25.4(a) to home-care
   * work. Omitted means "not established", and the stricter cl 25.4(a) reading
   * applies — never assume home care in order to exempt.
   */
  serviceContext?: 'HOME_CARE' | 'RESIDENTIAL' | null;
  /**
   * True when this period of work immediately precedes or follows a
   * cl 25.7 sleepover. Both flanks must be marked for PR798459 cl 25.4(c)
   * to treat them as one shift for the 10-hour rest check.
   */
  hasSleepover?: boolean;
  /** SCHADS cl 25.10 remote work — cl 10.5 and cl 25.4 do not apply. */
  isRemoteWork?: boolean;
}

/**
 * Validate a SINGLE shift against SCHADS minimum engagement + spread-of-hours
 * rules. For multi-shift rules (consecutive days, weekly 38h, min break
 * between shifts), use `validateWeekRoster`.
 */
export function validateShiftCompliance(
  shift: RosterShiftForCompliance,
  employmentType: 'permanent-full-time' | 'permanent-part-time' | 'casual',
): RosterComplianceIssue[] {
  const issues: RosterComplianceIssue[] = [];
  const durationMs = shift.endTime.getTime() - shift.startTime.getTime();
  const durationHours = durationMs / (1000 * 60 * 60);
  const paidHours = durationHours - (shift.breakMinutes ?? 0) / 60;

  // cl 25.10(f)(ii): remote work has its own minimum ladder; cl 10.5 does not apply.
  if (shift.isRemoteWork) {
    return issues;
  }

  // Minimum engagement — SCHADS cl 10.5(a) PT/casual SACS non-disability (3h)
  // + cl 10.5(b) PT/casual home care + community + SACS disability (2h).
  // Phase 1.4 Cluster E1 (2026-05-17) refinement: full-time employees do
  // NOT have a per-shift minimum payment under cl 10.5 — they are paid
  // guaranteed 38h. The fullTime: 3 default is a Statura-internal rostering
  // advisory, not Award-mandated. Message wording reflects this.
  const minRequired =
    employmentType === 'casual'
      ? SCHADS_MINIMUM_SHIFT_HOURS.casual
      : employmentType === 'permanent-part-time'
        ? SCHADS_MINIMUM_SHIFT_HOURS.partTime
        : SCHADS_MINIMUM_SHIFT_HOURS.fullTime;
  if (paidHours < minRequired) {
    const cite =
      employmentType === 'casual' || employmentType === 'permanent-part-time'
        ? 'SCHADS cl 10.5'
        : 'Statura rostering default — cl 10.5 does NOT establish a per-shift minimum for full-time';
    issues.push({
      code: 'MIN_ENGAGEMENT',
      message: `Shift is ${paidHours.toFixed(2)}h but minimum engagement for ${employmentType} is ${minRequired}h (${cite}).`,
      shiftId: shift.id,
    });
  }

  // Spread of hours — broken-shift span cap, SCHADS cl 25.6(f).
  // Phase 1.4 Cluster E1 (2026-05-17) citation correction (was cl 25.2 which
  // is the span-of-hours definition for ordinary day work, not the broken-
  // shift span cap).
  if (durationHours > SCHADS_ROSTER_LIMITS.spreadOfHoursMax) {
    issues.push({
      code: 'SPREAD_OF_HOURS',
      message: `Shift spreads ${durationHours.toFixed(2)}h (start to end) but SCHADS cl 25.6(f) broken-shift span max is ${SCHADS_ROSTER_LIMITS.spreadOfHoursMax}h.`,
      shiftId: shift.id,
    });
  }

  // Max daily ordinary hours (SCHADS clause 25.2(c): 10h max)
  if (paidHours > SCHADS_ROSTER_LIMITS.maxOrdinaryHoursPerDay) {
    issues.push({
      code: 'MAX_DAILY_HOURS',
      message: `Shift is ${paidHours.toFixed(2)}h of ordinary time but SCHADS max is ${SCHADS_ROSTER_LIMITS.maxOrdinaryHoursPerDay}h/day. Hours over this threshold must be paid as overtime.`,
      shiftId: shift.id,
    });
  }

  return issues;
}

/**
 * Validate an entire worker's week of shifts against SCHADS multi-shift rules:
 *  - Minimum 10-hour break between shifts (clause 25.4(a))
 *  - Maximum 5 consecutive days (SCHADS_ROSTER_LIMITS)
 *
 * @param shifts — the week's shifts
 * @param employmentType — permanent-full-time | permanent-part-time | casual
 * @param timezone — IANA zone (e.g. 'Australia/Sydney'). Required to correctly
 *   group shifts into calendar days. Defaults to Australia/Sydney. Without the
 *   right zone, consecutive-day grouping misfires for shifts crossing the
 *   UTC-day boundary (e.g. a 2026-01-02 06:00 AEST shift renders as
 *   "2026-01-01" in UTC and appears to be the same day as a 2026-01-01 22:00
 *   AEST shift 8h earlier). Tenant timezone should be sourced from
 *   tenant_configs.timezone by the caller.
 */
export function validateWeekRoster(
  shifts: RosterShiftForCompliance[],
  employmentType: 'permanent-full-time' | 'permanent-part-time' | 'casual',
  timezone: string = DEFAULT_TZ,
): RosterComplianceIssue[] {
  const issues: RosterComplianceIssue[] = [];

  // Validate each shift in isolation first
  for (const shift of shifts) {
    issues.push(...validateShiftCompliance(shift, employmentType));
  }

  // Sort shifts by start time for cross-shift checks
  const sorted = [...shifts].sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

  // Minimum break between shifts.
  //
  // cl 25.6(a)(i) makes two same-day home-care visits periods of work within ONE
  // broken shift, and cl 25.6(g) places the ten-hour rest "between broken shifts
  // rostered on successive days". Applying cl 25.4(a) between those periods
  // reports a rest breach for every consecutive visit — the same defect the
  // publish gate and the assignment blocker both carried. This is the third
  // layer; all three now agree.
  //
  // The same-day test uses the TENANT-LOCAL date of each shift's start, not a
  // stored header column, so a tenant east or west of UTC cannot have visits
  // split across a false day boundary.
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    if (!prev || !curr) continue;
    // Match roster-gate-detection: null context + no residential signal is
    // home-care style so missing client_id does not reintroduce visit-to-visit
    // false rest breaches.
    const prevIsResidential = prev.serviceContext === 'RESIDENTIAL';
    const currIsResidential = curr.serviceContext === 'RESIDENTIAL';
    const prevIsHomeCareStyle =
      prev.serviceContext === 'HOME_CARE' || (prev.serviceContext == null && !prevIsResidential);
    const currIsHomeCareStyle =
      curr.serviceContext === 'HOME_CARE' || (curr.serviceContext == null && !currIsResidential);
    if (
      prevIsHomeCareStyle &&
      currIsHomeCareStyle &&
      dateOnlyInTz(prev.startTime, timezone) === dateOnlyInTz(curr.startTime, timezone)
    ) {
      continue;
    }
    // PR798459 cl 25.4(c): sleepover is not a rest break; flanking work is
    // the same shift. Pre-1 June 2026 keeps the 10-hour reading.
    const wrapGapHours = (curr.startTime.getTime() - prev.endTime.getTime()) / (1000 * 60 * 60);
    if (
      isSleepoverFlankSameShift({
        prevHasSleepover: Boolean(prev.hasSleepover),
        currHasSleepover: Boolean(curr.hasSleepover),
        laterShiftDateYmd: dateOnlyInTz(curr.startTime, timezone),
        gapHours: wrapGapHours,
      })
    ) {
      continue;
    }
    // cl 25.10(f)(iii)(B): remote work does not count as work for cl 25.4 rest.
    if (remoteWorkDoesNotCountForRest(Boolean(prev.isRemoteWork), Boolean(curr.isRemoteWork))) {
      continue;
    }
    const breakHours = wrapGapHours;
    if (breakHours < SCHADS_ROSTER_LIMITS.minimumBreakBetweenShifts) {
      issues.push({
        code: 'MIN_BREAK_BETWEEN_SHIFTS',
        message: `Only ${breakHours.toFixed(2)}h break between shifts ${prev.id ?? '(unknown)'} and ${curr.id ?? '(unknown)'} — SCHADS clause 25.4(a) requires ${SCHADS_ROSTER_LIMITS.minimumBreakBetweenShifts}h minimum.`,
        shiftId: curr.id,
      });
    }
  }

  // Max consecutive days worked — calendar days in the tenant timezone.
  // De-duplicate so multiple shifts on the same calendar day don't inflate
  // the consecutive count, then check for runs of (consecutiveDaysMax + 1).
  if (sorted.length >= SCHADS_ROSTER_LIMITS.consecutiveDaysMax + 1) {
    const uniqueDayStrings = Array.from(
      new Set(sorted.map((s) => dateOnlyInTz(s.startTime, timezone))),
    );
    for (
      let i = 0;
      i <= uniqueDayStrings.length - (SCHADS_ROSTER_LIMITS.consecutiveDaysMax + 1);
      i++
    ) {
      const start = parseYmdAsUtc(uniqueDayStrings[i]!);
      const end = parseYmdAsUtc(uniqueDayStrings[i + SCHADS_ROSTER_LIMITS.consecutiveDaysMax]!);
      const diffDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
      if (diffDays === SCHADS_ROSTER_LIMITS.consecutiveDaysMax) {
        issues.push({
          code: 'MAX_CONSECUTIVE_DAYS',
          // Phase 1.4 Cluster E1 (2026-05-17): consecutiveDaysMax has NO Award
          // primary-source anchor (cl 25.3 mandates 2 rest days per week, not
          // a max consecutive work days). Advisory framed as Statura-internal
          // fatigue check, not Award compliance.
          message: `Worker has ${SCHADS_ROSTER_LIMITS.consecutiveDaysMax + 1} consecutive days of shifts starting ${uniqueDayStrings[i]} — Statura fatigue/rest advisory (no SCHADS Award maximum; cl 25.3 mandates 2 rest days/week).`,
        });
        break;
      }
    }
  }

  return issues;
}

/**
 * Parse a YYYY-MM-DD string as UTC midnight millis. Safe for day-arithmetic
 * (subtracting two of these gives a multiple of 24h, DST-independent, because
 * both sides are UTC-anchored). Do NOT use `new Date('YYYY-MM-DD')` directly
 * inside the validator — that's locale-dependent and can shift by an hour at
 * DST boundaries.
 */
function parseYmdAsUtc(ymd: string): number {
  const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
  return Date.UTC(y!, m! - 1, d!);
}

// ═══════════════════════════════════════════════════════════════════════════
// Weekly allowance cap aggregator (SCHADS MA000100 cl 20.3, 20.8)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Input to `applyWeeklyAllowanceCaps` — one entry per timesheet row whose
 * per-shift allowance lines need weekly-cap enforcement.
 *
 * The caller is responsible for bucketing shifts into SCHADS weeks by
 * supplying a stable `weekKey` (typically `"${staffId}|${mondayYMD}"`).
 * This keeps the aggregator pure and avoids tz assumptions.
 */
export interface ShiftForCap {
  /** Caller-defined stable id for this shift (e.g. timesheet row id). */
  shiftKey: string;
  /** Caller-defined bucket id — typically `${staffId}|${weekStartYMD}`. */
  weekKey: string;
  /** Per-shift allowance lines as emitted by calculateShiftPay. */
  allowanceLines: ShiftPayAllowanceLine[];
}

/**
 * Result of applying SCHADS weekly caps to a single shift's allowance
 * lines. Codes not subject to a weekly cap (sleepover, broken shift,
 * travel, on-call, delayed meal) pass through unchanged.
 *
 * Phase 1.5 Cluster 3A.2 (2026-05-17) — `capDeductions` keys are canonical
 * `_ALLOWANCE`-suffixed codes per Phase 1N taxonomy. Historical pre-1.5
 * rows in `pay_run_items.weekly_cap_applications` JSONB hold old keys
 * (`FIRST_AID`, `UNIFORM`, `LAUNDRY`) and are NEVER rewritten per the
 * audit-correct date-cutover policy. Consumers reading historical rows
 * must canonicalize via `canonicalAllowanceCode()`.
 */
export type CappedAllowanceCode = 'FIRST_AID_ALLOWANCE' | 'UNIFORM_ALLOWANCE' | 'LAUNDRY_ALLOWANCE';

export interface CappedShiftResult {
  shiftKey: string;
  weekKey: string;
  /** Lines after applying the weekly cap — same codes, amounts possibly clamped. */
  cappedLines: ShiftPayAllowanceLine[];
  /**
   * Per-code dollar amount that this shift lost due to the cap. Positive
   * value means the shift's per-shift amount was reduced. Empty when
   * nothing on this shift hit the cap.
   */
  capDeductions: Partial<Record<CappedAllowanceCode, number>>;
}

/**
 * Clamp weekly-capped SCHADS allowance codes across a group of shifts.
 *
 * Per Fair Work Pay Guide G01002983 (1 October 2025):
 *   • First-aid (cl 20.8) caps at $20.46/week — applies to FT weekly
 *     allowance AND PT/casual hourly accrual (hourly × hours).
 *   • Uniform (cl 20.3) caps at $6.24/week.
 *   • Laundry (cl 20.3) caps at $1.49/week.
 *
 * Algorithm (per weekKey × per capped-code):
 *   1. Walk the shifts in input order.
 *   2. Keep a running sum of that code's amount.
 *   3. When the next line would push the sum over the cap, clamp that
 *      line to the remaining headroom (minimum 0) and set every
 *      subsequent same-code line in the group to 0.
 *
 * The input order matters — callers should sort shifts chronologically
 * within each weekKey so the earliest shifts keep their full amounts
 * and the cap bites the latest shifts. This matches how payroll
 * officers typically reason about "I've already been paid my uniform
 * allowance for the week; additional shifts get nothing extra".
 */
export function applyWeeklyAllowanceCaps(
  shifts: ShiftForCap[],
  override?: SchadsRatesOverride,
): CappedShiftResult[] {
  // Phase 1 §4.3: cap amounts must come from the resolved rate set so EBA
  // overrides bind correctly. Without this, a tenant whose EBA raises the
  // weekly uniform cap (say) would have shifts clamped to the national
  // default — producing wrong net pay.
  //
  // Phase 1.5 Cluster 3A.2 (2026-05-17) — REGRESSION FIX (P0 silent over-pay).
  // Pre-fix, `cappedCodes` iterated old code strings ('FIRST_AID', 'UNIFORM',
  // 'LAUNDRY') and the inner loop did exact `lines[li].code !== code` match.
  // Phase 1N renamed engine emissions to canonical `_ALLOWANCE`-suffixed
  // names; engines emit 'FIRST_AID_ALLOWANCE' etc. so the cap loop SILENTLY
  // never matched → no clamping → workers over-paid the FWO Pay Guide
  // weekly maxima. Tests passed because fixtures still used old codes.
  // Phase 1.5 fix: canonicalize the line code via `canonicalAllowanceCode`
  // before comparing to the canonical cap-code list. Both historical and
  // canonical emissions converge.
  const rates = resolveRates(override);
  const cappedCodes: Array<{
    code: CappedAllowanceCode;
    weeklyCap: number;
  }> = [
    { code: 'FIRST_AID_ALLOWANCE', weeklyCap: rates.allowances.firstAidWeekly },
    { code: 'UNIFORM_ALLOWANCE', weeklyCap: rates.allowances.uniformWeekly },
    { code: 'LAUNDRY_ALLOWANCE', weeklyCap: rates.allowances.laundryWeeklyCap },
  ];

  // Seed the output: clone every line so we don't mutate caller state.
  const results: CappedShiftResult[] = shifts.map((s) => ({
    shiftKey: s.shiftKey,
    weekKey: s.weekKey,
    cappedLines: s.allowanceLines.map((l) => ({ ...l })),
    capDeductions: {},
  }));

  // For each capped code, walk shifts per week and clamp running totals.
  for (const { code, weeklyCap } of cappedCodes) {
    // Group input-order indices by weekKey so we preserve the caller's
    // chronological intent within a week.
    const indicesByWeek = new Map<string, number[]>();
    for (let i = 0; i < shifts.length; i++) {
      const wk = shifts[i]!.weekKey;
      if (!indicesByWeek.has(wk)) indicesByWeek.set(wk, []);
      indicesByWeek.get(wk)!.push(i);
    }

    for (const [, indices] of indicesByWeek) {
      let running = 0;
      for (const i of indices) {
        const lines = results[i]!.cappedLines;
        for (let li = 0; li < lines.length; li++) {
          // Phase 1.5 Cluster 3A.2: canonicalize the input line code so both
          // pre-Phase-1N historical reads ('FIRST_AID') and post-Phase-1N
          // engine emissions ('FIRST_AID_ALLOWANCE') match the same cap.
          if (canonicalAllowanceCode(lines[li]!.code) !== code) continue;
          const original = lines[li]!.amount;
          const headroom = Math.max(0, weeklyCap - running);
          if (original <= headroom) {
            // Fits under the cap — keep as-is, advance running.

            running += original;
          } else {
            // Would exceed cap — clamp to headroom (may be 0).
            const clamped = round2(headroom);

            const deducted = round2(original - clamped);
            lines[li] = { ...lines[li]!, amount: clamped };
            results[i]!.capDeductions[code] = round2(
              (results[i]!.capDeductions[code] ?? 0) + deducted,
            );

            running += clamped;
          }
        }
      }
    }
  }

  return results;
}

// ═══════════════════════════════════════════════════════════════════════
// Vehicle cents-per-km STP tax split (cl 20.7(a) + ATO PAYGW rules)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Split a paid cents-per-km vehicle allowance into two STP Phase 2 lines:
 *   • `KM_ALLOWANCE_EXEMPT`  — PAYGW-exempt component, up to the ATO approved
 *     rate (`ATO_CENTS_PER_KM_2026_27`). STP allowance type 'CD'.
 *   • `KM_ALLOWANCE_TAXABLE` — taxable residual (paid rate − ATO rate) × km.
 *     PAYG withheld. STP allowance type 'CD'.
 *
 * The two amounts always sum to `paidRatePerKm × km` — the split changes STP
 * reporting and withholding, not gross pay. This is the per-shift split; it
 * assumes the whole shift's km sit under the 5,000 km/FY exemption limit.
 * `applyVehicleKmFyExemptionCap` reallocates exempt→taxable once an employee's
 * cumulative FY business km cross that limit (the engine has no cross-shift
 * context). Throws if `paidRatePerKm` is below the SCHADS cl 20.7(a) award
 * floor — paying under the award minimum is a compliance defect, not a split.
 */
export function splitVehicleKmLines(km: number, paidRatePerKm: number): ShiftPayAllowanceLine[] {
  if (!(km > 0)) return [];
  if (paidRatePerKm < SCHADS_VEHICLE_AWARD_FLOOR_2026_27) {
    throw new Error(
      `Vehicle km paid rate $${paidRatePerKm.toFixed(2)}/km is below the SCHADS cl 20.7(a) award floor $${SCHADS_VEHICLE_AWARD_FLOOR_2026_27.toFixed(2)}/km (eff 1 Jul 2026).`,
    );
  }
  const exemptRate = Math.min(ATO_CENTS_PER_KM_2026_27, paidRatePerKm);
  // eslint-disable-next-line statura/no-floating-point-money -- display rate only (shown in the line description); the paid amounts are round2-scaled below
  const taxableRate = round2(paidRatePerKm - exemptRate);
  // Round the PAID TOTAL first, then derive the taxable residual from it, so
  // exempt + taxable == the reported total to the cent even under IEEE-754 (a
  // residual off the RAW km×rate can lose a cent on fractional km, e.g.
  // 0.5km × $1.01: raw 0.505−0.46 = 0.04499… → $0.04, breaking the sum).
  // eslint-disable-next-line statura/no-floating-point-money -- canonical ATO/FWC per-km calc
  const paidTotal = round2(km * paidRatePerKm);
  // eslint-disable-next-line statura/no-floating-point-money -- canonical ATO/FWC per-km calc
  const exemptAmount = round2(km * exemptRate);
  // eslint-disable-next-line statura/no-floating-point-money -- residual of two cents-grid values; recovers the exact cent
  const taxableAmount = round2(paidTotal - exemptAmount);
  return [
    {
      code: 'KM_ALLOWANCE_EXEMPT',
      clauseRef: 'cl 20.7(a)',
      description: `Vehicle allowance — PAYGW-exempt (${km.toFixed(1)} km × $${exemptRate.toFixed(2)}/km)`,
      amount: exemptAmount,
      perShift: false,
      units: km,
      unitType: 'km',
      taxable: false,
      stpCategory: 'CD',
    },
    {
      code: 'KM_ALLOWANCE_TAXABLE',
      clauseRef: 'cl 20.7(a)',
      description: `Vehicle allowance — taxable residual (${km.toFixed(1)} km × $${taxableRate.toFixed(2)}/km)`,
      amount: taxableAmount,
      perShift: false,
      units: km,
      unitType: 'km',
      taxable: true,
      stpCategory: 'CD',
    },
  ];
}

/**
 * Input to `applyVehicleKmFyExemptionCap` — one entry per timesheet row whose
 * vehicle-km allowance was split (`vehicleKmTaxSplit`). The caller buckets by a
 * stable per-employee-per-financial-year `fyKey` (e.g. `"${staffId}|${fyStart}"`)
 * and supplies each shift's business `km` so the aggregator can track the 5,000
 * km/FY running total.
 */
export interface VehicleKmShiftForCap {
  shiftKey: string;
  fyKey: string;
  km: number;
  allowanceLines: ShiftPayAllowanceLine[];
}

export interface VehicleKmCappedShiftResult {
  shiftKey: string;
  fyKey: string;
  /** Lines after applying the FY exemption cap — same codes, exempt/taxable amounts possibly reallocated. */
  cappedLines: ShiftPayAllowanceLine[];
  /** km on this shift that fell BEYOND the 5,000 km/FY exempt limit. */
  excessKm: number;
  /** Dollars moved from the exempt line to the taxable line on this shift. */
  reallocatedToTaxable: number;
}

/**
 * Enforce the ATO 5,000 business-km/FY exemption limit across an employee's
 * split vehicle-km lines. Per the ATO rules, once cumulative business km for an
 * employee in a financial year exceed 5,000, the WHOLE per-km payment on the
 * excess km (including the otherwise-exempt component) becomes PAYGW-liable, so
 * the exempt dollars for the excess km move to the taxable line.
 *
 * Mirrors `applyWeeklyAllowanceCaps`: pure, order-sensitive (callers sort shifts
 * chronologically within an `fyKey` so the earliest km keep the exemption), and
 * clones lines rather than mutating caller state. Total pay per shift is
 * preserved — only the exempt/taxable division changes.
 */
export function applyVehicleKmFyExemptionCap(
  shifts: VehicleKmShiftForCap[],
): VehicleKmCappedShiftResult[] {
  const results: VehicleKmCappedShiftResult[] = shifts.map((s) => ({
    shiftKey: s.shiftKey,
    fyKey: s.fyKey,
    cappedLines: s.allowanceLines.map((l) => ({ ...l })),
    excessKm: 0,
    reallocatedToTaxable: 0,
  }));

  const indicesByFy = new Map<string, number[]>();
  for (let i = 0; i < shifts.length; i++) {
    const fy = shifts[i]!.fyKey;
    if (!indicesByFy.has(fy)) indicesByFy.set(fy, []);
    indicesByFy.get(fy)!.push(i);
  }

  for (const [, indices] of indicesByFy) {
    let runningKm = 0;
    for (const i of indices) {
      const shiftKm = shifts[i]!.km;
      const exemptKmAllowed = Math.max(0, Math.min(shiftKm, SCHADS_KM_FY_EXEMPT_LIMIT - runningKm));
      // Trigger on the EXACT overage, not a cent-rounded value — a shift whose
      // overage rounds to 0 km (e.g. 0.004 km past the limit) can still owe a
      // 1-cent exempt->taxable reallocation. The `reallocated <= 0` guard below
      // no-ops when no whole cent actually moves.
      const rawExcessKm = shiftKm - exemptKmAllowed;
      // Accumulate only non-negative km — a spurious negative shift must not
      // claw back FY headroom and re-exempt later km (km is clamped >=0 upstream,
      // but guard here so this pure function can't be corrupted by bad input).
      runningKm += Math.max(0, shiftKm);
      if (rawExcessKm <= 0 || shiftKm <= 0) continue;
      // Report the overage regardless of whether a whole cent moves below.
      results[i]!.excessKm = round2(rawExcessKm);

      const lines = results[i]!.cappedLines;
      const exemptIdx = lines.findIndex((l) => l.code === 'KM_ALLOWANCE_EXEMPT');
      const taxableIdx = lines.findIndex((l) => l.code === 'KM_ALLOWANCE_TAXABLE');
      if (exemptIdx < 0) continue;

      const exemptLine = lines[exemptIdx]!;
      const taxableLine = taxableIdx >= 0 ? lines[taxableIdx]! : null;
      // The shift's paid total across the two km lines — cents-grid values, so
      // this round2 is identity. Deriving the new taxable from this total keeps
      // exempt + taxable == the paid total to the cent after reallocation.
      const shiftPaidTotal = round2(exemptLine.amount + (taxableLine?.amount ?? 0));
      // Exempt dollars are the ATO rate on the km still WITHIN the FY limit,
      // recomputed from first principles — NOT a rescale of the already-rounded
      // exempt amount, which double-rounds and can misclassify a cent between
      // exempt and taxable at fractional-km boundaries.

      const newExempt = round2(ATO_CENTS_PER_KM_2026_27 * exemptKmAllowed);
      const reallocated = round2(exemptLine.amount - newExempt);
      if (reallocated <= 0) continue;

      const newTaxable = round2(shiftPaidTotal - newExempt);
      lines[exemptIdx] = { ...exemptLine, amount: newExempt };
      if (taxableLine) {
        lines[taxableIdx] = { ...taxableLine, amount: newTaxable };
      } else {
        // No taxable residual line existed (paid rate == exempt rate) — create one.
        lines.push({
          code: 'KM_ALLOWANCE_TAXABLE',
          clauseRef: exemptLine.clauseRef ?? 'cl 20.7(a)',
          description: `Vehicle allowance — taxable (over 5,000 km/FY)`,
          amount: newTaxable,
          perShift: false,
          units: round2(rawExcessKm),
          unitType: 'km',
          taxable: true,
          stpCategory: 'CD',
        });
      }
      results[i]!.reallocatedToTaxable = reallocated;
    }
  }

  return results;
}
